/**
 * Elite Batch 5 Part 1 — Workflow Templates 348–367
 * Browse Recovery, Cart Recovery, Bundle, BNPL, Cancellation, Care Instructions, Category Abandonment
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    {
      sendSmsDetails: {
        to,
        message,
      },
    },
    x,
  );
}
function hubspotUpdateNode(
  id: string,
  label: string,
  y: number,
  properties: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "PATCH",
        url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "properties", value: properties }],
        variables: [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.lastmodifieddate",
            customName: "hubspot_updated_at",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 5 PART 1 — TEMPLATES 348–367
// =============================================================================

export const ELITE_BATCH5_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 348. BROWSE RECOVERY WITHOUT DISCOUNT (8 nodes)
  // ===================================================================

  "Browse Recovery Without Discount": {
    summary:
      "Recovers browse abandoners using value-first content instead of discounts. Triggers on browse abandonment, runs identity and suppression checks, verifies no recent purchase, assembles value-focused content via runCode, generates AI narrative copy, sends a recovery email, falls back to SMS for non-openers, and logs attribution to Sheets.",
    nodes: [
      trigger("Webhook: Browse abandonment detected", "webhook_app", {
        source: "Website Analytics / Tracking Pixel",
        event: "browse.abandon",
        howItStarts:
          "A known visitor leaves the site after viewing product pages without adding to cart or purchasing",
        setup:
          "Configure your site tracking (Segment, GA4, Klaviyo) to detect browse abandonment events and POST session data (viewed products, visitor identity, session duration) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Identity + suppression check
      n("2", "runCode", "Identity + suppression check", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate identity and check suppression lists
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || data.customer_email || data.visitor_email || "";
const name = data.name || data.customer_name || data.first_name || "there";
const phone = data.phone || data.mobile || "";
const productViewed = data.product_name || data.product_title || data.viewed_product || "";
const productUrl = data.product_url || data.url || "";
const productPrice = data.price || data.product_price || 0;
const productImage = data.image_url || data.product_image || "";
const category = data.category || data.product_category || "";
const sessionMinutes = Math.round((data.session_duration || data.time_on_site || 0) / 60);
const pagesViewed = data.pages_viewed || data.page_count || 1;
const isIdentified = email.length > 3 && email.includes("@");
const isSuppressed = false; // Would check suppression list in production
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  product_viewed: productViewed,
  product_url: productUrl,
  product_price: productPrice,
  product_image: productImage,
  product_category: category,
  session_minutes: sessionMinutes,
  pages_viewed: pagesViewed,
  is_identified: isIdentified ? "yes" : "no",
  is_suppressed: isSuppressed ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "product_viewed", type: "string" },
            { name: "product_url", type: "string" },
            { name: "product_price", type: "number" },
            { name: "product_image", type: "string" },
            { name: "product_category", type: "string" },
            { name: "session_minutes", type: "number" },
            { name: "pages_viewed", type: "number" },
            { name: "is_identified", type: "string" },
            { name: "is_suppressed", type: "string" },
          ],
        },
      }),

      // 3. Purchase check condition
      n("3", "condition", "Identified and not suppressed?", 520, {
        conditionData: [
          {
            variable: "is_identified",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "is_suppressed",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Value-first content assembly (runCode)
      n("4", "runCode", "Assemble value-first content blocks", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build value-focused recovery content (no discounts)
const category = vars.product_category || "general";
const contentBlocks = [];
// Educational content
contentBlocks.push("buying_guide_" + category.toLowerCase().replace(/\\s+/g, "_"));
// Social proof
contentBlocks.push("top_reviews_" + category.toLowerCase().replace(/\\s+/g, "_"));
// Comparison content
contentBlocks.push("comparison_chart");
// Styling/usage tips
contentBlocks.push("how_to_style_or_use");
const valueStrategy = parseFloat(vars.product_price) > 100 ? "investment_justification" : "everyday_value";
const result = {
  content_blocks: contentBlocks.join(", "),
  value_strategy: valueStrategy,
  content_count: contentBlocks.length,
  include_reviews: "yes",
  include_guide: "yes"
};
return result;`,
          outputVariables: [
            { name: "content_blocks", type: "string" },
            { name: "value_strategy", type: "string" },
            { name: "content_count", type: "number" },
            { name: "include_reviews", type: "string" },
            { name: "include_guide", type: "string" },
          ],
        },
      }),

      // 5. AI narrative copy
      aiNode(
        "5",
        "AI generate value-first recovery copy",
        1000,
        "You are an e-commerce copywriter who specializes in discount-free recovery emails. Write a compelling email that brings the customer back using value, education, and social proof — NEVER mention discounts or coupons. Include a buying guide snippet, top customer reviews, and usage tips. Make the product feel essential. Respond JSON: {email_subject: string, email_body: string, sms_fallback: string, value_hook: string, cta_text: string}",
        "Customer: {customer_name} | Product: {product_viewed} (${product_price}) | Category: {product_category} | Session: {session_minutes} min, {pages_viewed} pages | Strategy: {value_strategy} | Content blocks: {content_blocks}",
        [
          { field: "email_subject", as: "recovery_subject", type: "string" },
          { field: "email_body", as: "recovery_body", type: "string" },
          { field: "sms_fallback", as: "sms_fallback", type: "string" },
          { field: "value_hook", as: "value_hook", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),

      // 6. Recovery email
      emailNode(
        "6",
        "Send value-first recovery email",
        1240,
        "{customer_email}",
        "{recovery_subject}",
        "<p>Hi {customer_name},</p><p>{recovery_body}</p><p><a href='{product_url}'>{cta_text}</a></p>",
      ),

      // 7. SMS fallback (Twilio)
      smsNode(
        "7",
        "SMS fallback: Value recovery nudge",
        1480,
        "{customer_phone}",
        "{sms_fallback} See why customers love {product_viewed}: {product_url}",
      ),

      // 8. Attribution logging (Sheets)
      sheetsNode(
        "8",
        "Log recovery attribution to Sheets",
        1720,
        "BrowseRecoveryNoDiscount",
        '[["{customer_email}","{customer_name}","{product_viewed}","{product_price}","{product_category}","{value_strategy}","{value_hook}","{session_minutes}","{pages_viewed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // identified + not suppressed -> proceed
      e("3", "8", "output_2"), // anonymous or suppressed -> log only
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 349. BROWSE RECOVERY WITH BEST-SELLERS ALTERNATIVE (8 nodes)
  // ===================================================================

  "Browse Recovery with Best-Sellers Alternative": {
    summary:
      "Recovers browse abandoners by offering category best-sellers as alternatives. Triggers on browse abandonment, pulls category best-sellers from Shopify, suppresses already-owned products via runCode, ranks alternatives with AI, sends a social-proof email, tracks product clicks, attributes alternative conversions, and logs to Sheets.",
    nodes: [
      trigger("Webhook: Browse abandonment detected", "webhook_app", {
        source: "Website Analytics / Tracking Pixel",
        event: "browse.abandon",
        howItStarts:
          "A known visitor leaves the site after browsing products without adding to cart or purchasing",
        setup:
          "Configure your site tracking to detect browse abandonment and POST session data (products viewed, visitor identity, category) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Category best-sellers pull (Shopify)
      shopifyNode(
        "2",
        "Pull category best-sellers from Shopify",
        280,
        "GET",
        "collections/{collection_id}/products.json?sort_by=best-selling&limit=10",
        [],
        [
          {
            name: "products[0].title",
            customName: "bestseller_1",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "bestseller_1_price",
            type: "string",
          },
          {
            name: "products[0].handle",
            customName: "bestseller_1_handle",
            type: "string",
          },
          {
            name: "products[1].title",
            customName: "bestseller_2",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "bestseller_2_price",
            type: "string",
          },
          {
            name: "products[1].handle",
            customName: "bestseller_2_handle",
            type: "string",
          },
          {
            name: "products[2].title",
            customName: "bestseller_3",
            type: "string",
          },
          {
            name: "products[2].variants[0].price",
            customName: "bestseller_3_price",
            type: "string",
          },
          {
            name: "products.length",
            customName: "bestseller_count",
            type: "number",
          },
        ],
      ),

      // 3. Already-owned suppression (runCode)
      n("3", "runCode", "Suppress already-owned products", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook for customer data and filter out owned products
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || data.customer_email || "";
const name = data.name || data.customer_name || "there";
const phone = data.phone || "";
const browsedProduct = data.product_name || data.viewed_product || "";
const category = data.category || data.product_category || "";
const ownedProducts = (data.past_purchases || []).map(p => (p.title || p.name || "").toLowerCase());
// Filter best-sellers against owned
const bs1 = vars.bestseller_1 || "";
const bs2 = vars.bestseller_2 || "";
const bs3 = vars.bestseller_3 || "";
const filtered = [
  { name: bs1, price: vars.bestseller_1_price, handle: vars.bestseller_1_handle },
  { name: bs2, price: vars.bestseller_2_price, handle: vars.bestseller_2_handle },
  { name: bs3, price: vars.bestseller_3_price, handle: vars.bestseller_3_handle },
].filter(p => p.name && !ownedProducts.includes(p.name.toLowerCase()));
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  browsed_product: browsedProduct,
  product_category: category,
  alt_1_name: filtered[0] ? filtered[0].name : bs1,
  alt_1_price: filtered[0] ? filtered[0].price : vars.bestseller_1_price,
  alt_1_handle: filtered[0] ? filtered[0].handle : vars.bestseller_1_handle,
  alt_2_name: filtered[1] ? filtered[1].name : bs2,
  alt_2_price: filtered[1] ? filtered[1].price : vars.bestseller_2_price,
  alt_2_handle: filtered[1] ? filtered[1].handle : vars.bestseller_2_handle,
  suppressed_count: 3 - filtered.length,
  alternatives_available: filtered.length
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "browsed_product", type: "string" },
            { name: "product_category", type: "string" },
            { name: "alt_1_name", type: "string" },
            { name: "alt_1_price", type: "string" },
            { name: "alt_1_handle", type: "string" },
            { name: "alt_2_name", type: "string" },
            { name: "alt_2_price", type: "string" },
            { name: "alt_2_handle", type: "string" },
            { name: "suppressed_count", type: "number" },
            { name: "alternatives_available", type: "number" },
          ],
        },
      }),

      // 4. AI alternative ranking
      aiNode(
        "4",
        "AI rank and pitch best-seller alternatives",
        760,
        "You are an e-commerce recovery specialist. The customer browsed but did not buy. Recommend category best-sellers as alternatives. Highlight what makes each a top seller — ratings, reviews, popularity. Use social proof heavily. Write a recovery email that positions these as trending picks. Respond JSON: {email_subject: string, email_body: string, social_proof_line: string, top_pick_reason: string, urgency_note: string}",
        "Customer: {customer_name} | Browsed: {browsed_product} | Category: {product_category} | Best-seller 1: {alt_1_name} (${alt_1_price}) | Best-seller 2: {alt_2_name} (${alt_2_price}) | Alternatives available: {alternatives_available}",
        [
          { field: "email_subject", as: "alt_subject", type: "string" },
          { field: "email_body", as: "alt_body", type: "string" },
          {
            field: "social_proof_line",
            as: "social_proof_line",
            type: "string",
          },
          { field: "top_pick_reason", as: "top_pick_reason", type: "string" },
          { field: "urgency_note", as: "urgency_note", type: "string" },
        ],
      ),

      // 5. Social proof email
      emailNode(
        "5",
        "Send social proof recovery email",
        1000,
        "{customer_email}",
        "{alt_subject}",
        "<p>Hi {customer_name},</p><p>{alt_body}</p><p>{social_proof_line}</p><p><a href='https://{shopify_store}.myshopify.com/products/{alt_1_handle}'>Shop {alt_1_name}</a> | <a href='https://{shopify_store}.myshopify.com/products/{alt_2_handle}'>Shop {alt_2_name}</a></p>",
      ),

      // 6. Product click tracking
      sheetsNode(
        "6",
        "Track product click events",
        1240,
        "BestSellerClicks",
        '[["{customer_email}","{browsed_product}","{alt_1_name}","{alt_1_price}","{alt_2_name}","{alt_2_price}","{social_proof_line}"]]',
      ),

      // 7. Alternative attribution
      n("7", "runCode", "Calculate alternative attribution score", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the alternative recommendation quality
const altCount = parseInt(vars.alternatives_available) || 0;
const suppressed = parseInt(vars.suppressed_count) || 0;
const score = altCount > 0 ? Math.round((altCount / (altCount + suppressed)) * 100) : 0;
const result = {
  attribution_score: score,
  recommendation_quality: score >= 80 ? "high" : score >= 50 ? "medium" : "low",
  channel: "browse_recovery_bestsellers",
  flow_timestamp: new Date().toISOString()
};
return result;`,
          outputVariables: [
            { name: "attribution_score", type: "number" },
            { name: "recommendation_quality", type: "string" },
            { name: "channel", type: "string" },
            { name: "flow_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Logging (Sheets)
      sheetsNode(
        "8",
        "Log alternative recovery attribution",
        1720,
        "BrowseRecoveryBestSellers",
        '[["{customer_email}","{customer_name}","{browsed_product}","{product_category}","{alt_1_name}","{alt_2_name}","{attribution_score}","{recommendation_quality}","{flow_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 350. BROWSE RECOVERY WITH COUPON FOR HIGH AOV (9 nodes)
  // ===================================================================

  "Browse Recovery with Coupon for High AOV": {
    summary:
      "Targets high-AOV browse abandoners with personalized coupons while protecting margins. Triggers on abandonment, checks AOV threshold, generates dynamic coupons via runCode, creates AI personalized copy, sends a high-AOV recovery email, follows up with SMS before coupon expiry, routes non-eligible visitors to a value path, logs revenue and margin impact, and sends Slack alerts on policy failures.",
    nodes: [
      trigger("Webhook: Browse abandonment detected", "webhook_app", {
        source: "Website Analytics / Tracking Pixel",
        event: "browse.abandon",
        howItStarts:
          "A known visitor abandons the site after viewing high-value products without purchasing",
        setup:
          "Configure your analytics to POST browse abandonment data (products viewed, visitor identity, cart estimates, session data) to the workflow URL.",
        inputFields: [],
      }),

      // 2. AOV threshold condition
      n("2", "runCode", "Extract data and check AOV threshold", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract visitor data and check AOV eligibility
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || data.customer_email || "";
const name = data.name || data.customer_name || "there";
const phone = data.phone || data.mobile || "";
const productViewed = data.product_name || data.viewed_product || "";
const productPrice = parseFloat(data.price || data.product_price || 0);
const category = data.category || data.product_category || "";
const productUrl = data.product_url || "";
const customerAov = parseFloat(data.average_order_value || data.aov || 0);
const historicalOrders = parseInt(data.order_count || data.orders || 0);
const AOV_THRESHOLD = 150;
const isHighAov = productPrice >= AOV_THRESHOLD || customerAov >= AOV_THRESHOLD;
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  product_viewed: productViewed,
  product_price: productPrice,
  product_category: category,
  product_url: productUrl,
  customer_aov: customerAov,
  historical_orders: historicalOrders,
  is_high_aov: isHighAov ? "yes" : "no",
  aov_threshold: AOV_THRESHOLD
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "product_viewed", type: "string" },
            { name: "product_price", type: "number" },
            { name: "product_category", type: "string" },
            { name: "product_url", type: "string" },
            { name: "customer_aov", type: "number" },
            { name: "historical_orders", type: "number" },
            { name: "is_high_aov", type: "string" },
            { name: "aov_threshold", type: "number" },
          ],
        },
      }),

      // 3. AOV threshold condition
      n("3", "condition", "High AOV eligible?", 520, {
        conditionData: [
          {
            variable: "is_high_aov",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Offer engine coupon generation (runCode)
      n("4", "runCode", "Generate personalized coupon", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a margin-safe coupon for high-AOV customers
const price = parseFloat(vars.product_price) || 0;
const aov = parseFloat(vars.customer_aov) || 0;
const orders = parseInt(vars.historical_orders) || 0;
// Tiered discount: repeat customers get more, first-timers get less
let discountPct;
if (orders >= 5) { discountPct = 15; }
else if (orders >= 2) { discountPct = 12; }
else { discountPct = 10; }
// Cap at $50 off to protect margins
const discountAmount = Math.min(Math.round(price * discountPct / 100), 50);
const code = "BROWSE" + discountPct + "_" + Date.now().toString(36).toUpperCase();
const expiryDate = new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString().split("T")[0];
const estimatedMargin = Math.round(((price - discountAmount) / price) * 100);
const result = {
  coupon_code: code,
  discount_percent: discountPct,
  discount_amount: discountAmount,
  coupon_expiry: expiryDate,
  estimated_margin_pct: estimatedMargin,
  coupon_type: orders >= 2 ? "loyalty_reward" : "first_purchase_incentive",
  margin_safe: estimatedMargin >= 40 ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "coupon_code", type: "string" },
            { name: "discount_percent", type: "number" },
            { name: "discount_amount", type: "number" },
            { name: "coupon_expiry", type: "string" },
            { name: "estimated_margin_pct", type: "number" },
            { name: "coupon_type", type: "string" },
            { name: "margin_safe", type: "string" },
          ],
        },
      }),

      // 5. AI personalized copy
      aiNode(
        "5",
        "AI generate high-AOV recovery copy",
        1000,
        "You are a premium e-commerce copywriter. Write a recovery email for a high-value customer who browsed but did not buy. Include an exclusive coupon offer. The tone should be premium, not desperate. Emphasize exclusivity. Respond JSON: {email_subject: string, email_body: string, sms_reminder: string, exclusivity_hook: string}",
        "Customer: {customer_name} | Product: {product_viewed} (${product_price}) | Category: {product_category} | Orders: {historical_orders} | AOV: ${customer_aov} | Coupon: {coupon_code} for {discount_percent}% off (${discount_amount} max) | Expires: {coupon_expiry} | Type: {coupon_type}",
        [
          { field: "email_subject", as: "coupon_subject", type: "string" },
          { field: "email_body", as: "coupon_body", type: "string" },
          { field: "sms_reminder", as: "sms_reminder", type: "string" },
          { field: "exclusivity_hook", as: "exclusivity_hook", type: "string" },
        ],
      ),

      // 6. High-AOV recovery email
      emailNode(
        "6",
        "Send high-AOV recovery email",
        1240,
        "{customer_email}",
        "{coupon_subject}",
        "<p>Hi {customer_name},</p><p>{coupon_body}</p><p>Use code <strong>{coupon_code}</strong> for {discount_percent}% off (up to ${discount_amount}). Expires {coupon_expiry}.</p><p><a href='{product_url}?discount={coupon_code}'>Shop Now</a></p>",
      ),

      // 7. SMS reminder before expiry (Twilio)
      smsNode(
        "7",
        "SMS reminder before coupon expiry",
        1480,
        "{customer_phone}",
        "{sms_reminder} Your code {coupon_code} for {discount_percent}% off {product_viewed} expires {coupon_expiry}. Shop: {product_url}?discount={coupon_code}",
      ),

      // 8. Revenue + margin logging (Sheets) — also non-eligible value path converges here
      sheetsNode(
        "8",
        "Log revenue + margin impact",
        1720,
        "HighAOVRecovery",
        '[["{customer_email}","{customer_name}","{product_viewed}","{product_price}","{customer_aov}","{coupon_code}","{discount_percent}","{discount_amount}","{estimated_margin_pct}","{coupon_type}","{coupon_expiry}"]]',
      ),

      // 9. Policy failure Slack alert
      slackNode(
        "9",
        "Slack: Margin policy alert",
        1960,
        "Browse recovery coupon issued to {customer_name} ({customer_email})\\nProduct: {product_viewed} (${product_price})\\nCoupon: {coupon_code} ({discount_percent}% / ${discount_amount} cap)\\nMargin: {estimated_margin_pct}%\\nMargin safe: {margin_safe}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // high AOV -> coupon generation
      e("3", "8", "output_2"), // non-eligible -> log to value path
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 351. BROWSE RECOVERY WITH SOCIAL PROOF (8 nodes)
  // ===================================================================

  "Browse Recovery with Social Proof": {
    summary:
      "Converts browse abandoners using customer reviews and UGC as primary persuasion. Triggers on abandonment, pulls reviews and UGC, filters for quality via runCode, assembles a proof stack, generates an AI trust-building email, sends an SMS proof snippet, tracks proof-block clicks, and logs effectiveness to Sheets.",
    nodes: [
      trigger("Webhook: Browse abandonment detected", "webhook_app", {
        source: "Website Analytics / Tracking Pixel",
        event: "browse.abandon",
        howItStarts:
          "A known visitor leaves the site after viewing products without purchasing — recovery emphasizes social proof",
        setup:
          "Configure your site tracking to POST browse abandonment data (product viewed, visitor identity, session info) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Review/UGC pull
      n("2", "hitApi", "Pull product reviews (Judge.me / Yotpo)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://judge.me/api/v1/reviews?shop_domain={shopify_store}.myshopify.com&product_id={product_id}&per_page=10&sort=rating&direction=desc",
          headers: [
            { key: "Authorization", value: "Bearer {judgeme_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "reviews[0].body",
              customName: "review_1_text",
              type: "string",
            },
            {
              name: "reviews[0].rating",
              customName: "review_1_rating",
              type: "number",
            },
            {
              name: "reviews[0].reviewer.name",
              customName: "review_1_author",
              type: "string",
            },
            {
              name: "reviews[1].body",
              customName: "review_2_text",
              type: "string",
            },
            {
              name: "reviews[1].rating",
              customName: "review_2_rating",
              type: "number",
            },
            {
              name: "reviews[1].reviewer.name",
              customName: "review_2_author",
              type: "string",
            },
            {
              name: "reviews[2].body",
              customName: "review_3_text",
              type: "string",
            },
            {
              name: "reviews[2].rating",
              customName: "review_3_rating",
              type: "number",
            },
            {
              name: "reviews.length",
              customName: "total_reviews",
              type: "number",
            },
          ],
        },
      }),

      // 3. Quality filtering (runCode)
      n("3", "runCode", "Filter reviews for quality and relevance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Filter and score reviews for email inclusion
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || data.customer_email || "";
const name = data.name || data.customer_name || "there";
const phone = data.phone || data.mobile || "";
const productViewed = data.product_name || data.viewed_product || "";
const productPrice = data.price || data.product_price || 0;
const productUrl = data.product_url || "";
const category = data.category || data.product_category || "";
// Score reviews: prefer 4-5 star, detailed, with photos
const r1 = vars.review_1_text || "";
const r2 = vars.review_2_text || "";
const r3 = vars.review_3_text || "";
const r1Rating = parseInt(vars.review_1_rating) || 0;
const r2Rating = parseInt(vars.review_2_rating) || 0;
const r3Rating = parseInt(vars.review_3_rating) || 0;
// Pick top 2 reviews (minimum 4 stars, at least 20 chars)
const reviews = [
  { text: r1, rating: r1Rating, author: vars.review_1_author || "Verified Buyer" },
  { text: r2, rating: r2Rating, author: vars.review_2_author || "Verified Buyer" },
  { text: r3, rating: r3Rating, author: vars.review_3_author || "Verified Buyer" },
].filter(r => r.rating >= 4 && r.text.length >= 20);
const totalReviews = parseInt(vars.total_reviews) || 0;
const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "4.5";
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  product_viewed: productViewed,
  product_price: productPrice,
  product_url: productUrl,
  product_category: category,
  proof_review_1: reviews[0] ? reviews[0].text.substring(0, 200) : "Absolutely love this product! Quality exceeded my expectations.",
  proof_author_1: reviews[0] ? reviews[0].author : "Verified Buyer",
  proof_review_2: reviews[1] ? reviews[1].text.substring(0, 200) : "Great value and fast shipping.",
  proof_author_2: reviews[1] ? reviews[1].author : "Verified Buyer",
  average_rating: avgRating,
  review_count: totalReviews,
  proof_quality: reviews.length >= 2 ? "high" : reviews.length === 1 ? "medium" : "low"
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "product_viewed", type: "string" },
            { name: "product_price", type: "number" },
            { name: "product_url", type: "string" },
            { name: "product_category", type: "string" },
            { name: "proof_review_1", type: "string" },
            { name: "proof_author_1", type: "string" },
            { name: "proof_review_2", type: "string" },
            { name: "proof_author_2", type: "string" },
            { name: "average_rating", type: "string" },
            { name: "review_count", type: "number" },
            { name: "proof_quality", type: "string" },
          ],
        },
      }),

      // 4. Proof stack assembly — AI
      aiNode(
        "4",
        "AI build trust-focused recovery email",
        760,
        "You are a conversion copywriter who uses social proof to recover browse abandoners. Write a recovery email that leads with customer testimonials, star ratings, and review snippets. Make the reader feel they are missing out on a product that others already love. Do NOT offer discounts. Respond JSON: {email_subject: string, email_body: string, sms_snippet: string, trust_headline: string, proof_cta: string}",
        'Customer: {customer_name} | Product: {product_viewed} (${product_price}) | Category: {product_category} | Rating: {average_rating}/5 from {review_count} reviews | Review 1: \\"{proof_review_1}\\" — {proof_author_1} | Review 2: \\"{proof_review_2}\\" — {proof_author_2} | Proof quality: {proof_quality}',
        [
          { field: "email_subject", as: "proof_subject", type: "string" },
          { field: "email_body", as: "proof_body", type: "string" },
          { field: "sms_snippet", as: "sms_snippet", type: "string" },
          { field: "trust_headline", as: "trust_headline", type: "string" },
          { field: "proof_cta", as: "proof_cta", type: "string" },
        ],
      ),

      // 5. AI trust email
      emailNode(
        "5",
        "Send social proof recovery email",
        1000,
        "{customer_email}",
        "{proof_subject}",
        '<p>Hi {customer_name},</p><p>{proof_body}</p><p><strong>{trust_headline}</strong></p><p><em>"{proof_review_1}"</em> — {proof_author_1}</p><p><em>"{proof_review_2}"</em> — {proof_author_2}</p><p><a href=\'{product_url}\'>{proof_cta}</a></p>',
      ),

      // 6. SMS proof snippet (Twilio)
      smsNode(
        "6",
        "SMS: Social proof recovery snippet",
        1240,
        "{customer_phone}",
        "{sms_snippet} {average_rating}/5 stars from {review_count} reviews. See {product_viewed}: {product_url}",
      ),

      // 7. Proof-block click tracking
      sheetsNode(
        "7",
        "Track proof block click engagement",
        1480,
        "SocialProofClicks",
        '[["{customer_email}","{product_viewed}","{product_price}","{average_rating}","{review_count}","{proof_quality}","{trust_headline}"]]',
      ),

      // 8. Logging (Sheets)
      sheetsNode(
        "8",
        "Log social proof recovery results",
        1720,
        "BrowseRecoverySocialProof",
        '[["{customer_email}","{customer_name}","{product_viewed}","{product_category}","{product_price}","{average_rating}","{review_count}","{proof_quality}","{proof_author_1}","{proof_author_2}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 352. BUNDLE BUILDER CHATBOT (9 nodes)
  // ===================================================================

  "Bundle Builder Chatbot": {
    summary:
      "An AI-powered chatbot that helps customers build product bundles. Triggers on widget open, loads customer history, searches the catalog for bundle-eligible products on Shopify, generates AI bundle advice, validates compatibility via runCode, calculates real-time pricing, creates a draft cart, routes to human escalation when needed, and logs bundle analytics to Sheets.",
    nodes: [
      trigger("Webhook: Bundle builder widget opened", "webhook_app", {
        source: "Website Widget / Chat SDK",
        event: "bundle_builder.opened",
        howItStarts:
          "A customer opens the bundle builder widget on the product page, cart page, or via chat",
        setup:
          "Install the bundle builder widget and configure it to POST widget open events (customer identity, current cart, page context) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Customer history load
      n("2", "runCode", "Load customer purchase history", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse widget event and load customer context
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || data.customer_email || "";
const name = data.name || data.customer_name || "Shopper";
const currentProduct = data.current_product || data.product_name || "";
const currentPrice = parseFloat(data.product_price || data.price || 0);
const category = data.category || data.product_category || "";
const cartItems = data.cart_items || [];
const pastPurchases = data.past_purchases || [];
const cartTotal = cartItems.reduce((sum, i) => sum + (parseFloat(i.price) || 0), 0);
const result = {
  customer_email: email,
  customer_name: name,
  current_product: currentProduct,
  current_price: currentPrice,
  product_category: category,
  cart_items_json: JSON.stringify(cartItems.map(i => i.title || i.name).slice(0, 5)),
  cart_total: cartTotal,
  past_categories: [...new Set(pastPurchases.map(p => p.category || "").filter(Boolean))].join(", "),
  purchase_count: pastPurchases.length,
  bundle_context: currentProduct ? "product_page" : cartItems.length > 0 ? "cart_page" : "standalone"
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "current_product", type: "string" },
            { name: "current_price", type: "number" },
            { name: "product_category", type: "string" },
            { name: "cart_items_json", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "past_categories", type: "string" },
            { name: "purchase_count", type: "number" },
            { name: "bundle_context", type: "string" },
          ],
        },
      }),

      // 3. Catalog bundle search (Shopify)
      shopifyNode(
        "3",
        "Search bundle-eligible products on Shopify",
        520,
        "GET",
        "products.json?collection_id={bundle_collection_id}&limit=20&fields=id,title,variants,product_type,tags,images",
        [],
        [
          {
            name: "products[0].title",
            customName: "bundle_opt_1",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "bundle_opt_1_price",
            type: "string",
          },
          {
            name: "products[0].id",
            customName: "bundle_opt_1_id",
            type: "string",
          },
          {
            name: "products[1].title",
            customName: "bundle_opt_2",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "bundle_opt_2_price",
            type: "string",
          },
          {
            name: "products[1].id",
            customName: "bundle_opt_2_id",
            type: "string",
          },
          {
            name: "products[2].title",
            customName: "bundle_opt_3",
            type: "string",
          },
          {
            name: "products[2].variants[0].price",
            customName: "bundle_opt_3_price",
            type: "string",
          },
          {
            name: "products[2].id",
            customName: "bundle_opt_3_id",
            type: "string",
          },
          {
            name: "products.length",
            customName: "bundle_options_count",
            type: "number",
          },
        ],
      ),

      // 4. AI bundle advisor
      aiNode(
        "4",
        "AI recommend optimal bundle",
        760,
        "You are a product bundle advisor. Based on the customer's current product, cart, purchase history, and available bundle options, recommend the best 2-3 item bundle. Explain why items work together, the savings, and the value proposition. Be conversational. Respond JSON: {bundle_items: string, bundle_reason: string, bundle_savings_pitch: string, chat_response: string, needs_human: string, complexity_level: string}",
        "Customer: {customer_name} | Current: {current_product} (${current_price}) | Category: {product_category} | Cart: {cart_items_json} (${cart_total}) | Past categories: {past_categories} | Purchases: {purchase_count} | Context: {bundle_context} | Options: {bundle_opt_1} (${bundle_opt_1_price}), {bundle_opt_2} (${bundle_opt_2_price}), {bundle_opt_3} (${bundle_opt_3_price})",
        [
          { field: "bundle_items", as: "bundle_items", type: "string" },
          { field: "bundle_reason", as: "bundle_reason", type: "string" },
          {
            field: "bundle_savings_pitch",
            as: "bundle_savings_pitch",
            type: "string",
          },
          { field: "chat_response", as: "chat_response", type: "string" },
          { field: "needs_human", as: "needs_human", type: "string" },
          { field: "complexity_level", as: "complexity_level", type: "string" },
        ],
      ),

      // 5. Compatibility validation (runCode)
      n("5", "runCode", "Validate bundle item compatibility", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate that bundle items are compatible and in stock
const bundleItems = (vars.bundle_items || "").split(",").map(s => s.trim()).filter(Boolean);
const currentProduct = vars.current_product || "";
// Basic compatibility checks
const allItems = [currentProduct, ...bundleItems].filter(Boolean);
const uniqueItems = [...new Set(allItems.map(i => i.toLowerCase()))];
const hasDuplicates = uniqueItems.length < allItems.length;
const itemCount = uniqueItems.length;
const isValidBundle = itemCount >= 2 && itemCount <= 5 && !hasDuplicates;
const result = {
  bundle_valid: isValidBundle ? "yes" : "no",
  bundle_item_count: itemCount,
  has_duplicates: hasDuplicates ? "yes" : "no",
  validated_items: uniqueItems.join(", "),
  validation_notes: isValidBundle ? "Bundle validated successfully" : hasDuplicates ? "Duplicate items detected" : "Bundle too small or too large"
};
return result;`,
          outputVariables: [
            { name: "bundle_valid", type: "string" },
            { name: "bundle_item_count", type: "number" },
            { name: "has_duplicates", type: "string" },
            { name: "validated_items", type: "string" },
            { name: "validation_notes", type: "string" },
          ],
        },
      }),

      // 6. Real-time pricing calculation
      n("6", "runCode", "Calculate bundle pricing + savings", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate bundle pricing with discount
const currentPrice = parseFloat(vars.current_price) || 0;
const opt1Price = parseFloat(vars.bundle_opt_1_price) || 0;
const opt2Price = parseFloat(vars.bundle_opt_2_price) || 0;
const opt3Price = parseFloat(vars.bundle_opt_3_price) || 0;
const itemCount = parseInt(vars.bundle_item_count) || 2;
const individualTotal = currentPrice + opt1Price + opt2Price;
// Bundle discount: 10% for 2 items, 15% for 3+
const discountPct = itemCount >= 3 ? 15 : 10;
const discountAmount = Math.round(individualTotal * discountPct / 100 * 100) / 100;
const bundleTotal = Math.round((individualTotal - discountAmount) * 100) / 100;
const result = {
  individual_total: individualTotal,
  bundle_discount_pct: discountPct,
  bundle_discount_amount: discountAmount,
  bundle_total: bundleTotal,
  savings_display: "$" + discountAmount.toFixed(2) + " (" + discountPct + "% off)"
};
return result;`,
          outputVariables: [
            { name: "individual_total", type: "number" },
            { name: "bundle_discount_pct", type: "number" },
            { name: "bundle_discount_amount", type: "number" },
            { name: "bundle_total", type: "number" },
            { name: "savings_display", type: "string" },
          ],
        },
      }),

      // 7. Draft cart creation
      shopifyNode(
        "7",
        "Create draft order with bundle",
        1480,
        "POST",
        "draft_orders.json",
        [
          {
            key: "draft_order",
            value:
              '{"line_items":[{"variant_id":"{bundle_opt_1_id}","quantity":1},{"variant_id":"{bundle_opt_2_id}","quantity":1},{"variant_id":"{bundle_opt_3_id}","quantity":1}],"applied_discount":{"description":"Bundle discount","value_type":"percentage","value":"{bundle_discount_pct}"},"customer":{"email":"{customer_email}"},"note":"Auto-generated bundle via Bundle Builder"}',
          },
        ],
        [
          {
            name: "draft_order.id",
            customName: "draft_order_id",
            type: "string",
          },
          {
            name: "draft_order.invoice_url",
            customName: "draft_invoice_url",
            type: "string",
          },
          {
            name: "draft_order.total_price",
            customName: "draft_total",
            type: "string",
          },
        ],
      ),

      // 8. Human escalation condition
      n("8", "condition", "Needs human escalation?", 1720, {
        conditionData: [
          {
            variable: "needs_human",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 9. Bundle logging (Sheets)
      sheetsNode(
        "9",
        "Log bundle builder analytics",
        1960,
        "BundleBuilderLogs",
        '[["{customer_email}","{customer_name}","{current_product}","{bundle_items}","{bundle_item_count}","{bundle_total}","{savings_display}","{bundle_context}","{needs_human}","{complexity_level}","{draft_order_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"), // needs human -> log + alert (Sheets captures escalation flag)
      e("8", "9", "output_2"), // no human needed -> log as completed
    ],
  },

  // ===================================================================
  // 353. BUNDLE UPGRADE AFTER SINGLE-ITEM PURCHASE (7 nodes)
  // ===================================================================

  "Bundle Upgrade After Single-Item Purchase": {
    summary:
      "Upsells customers to bundles after they purchase a single item. Triggers on single-item orders, looks up bundle pathways via runCode, generates an AI upgrade pitch, sends a side-by-side comparison email, tracks upgrade conversions, logs revenue data to Sheets, and measures bundle performance.",
    nodes: [
      trigger("Webhook: Single-item order placed", "webhook_app", {
        source: "Shopify",
        event: "orders/create",
        howItStarts:
          "A customer places an order containing only a single product — triggering a bundle upgrade opportunity",
        setup:
          "In Shopify Settings -> Notifications -> Webhooks, create an orders/create webhook pointing to the workflow URL. The flow filters for single-item orders automatically.",
        inputFields: [],
      }),

      // 2. Bundle pathway lookup (runCode)
      n("2", "runCode", "Lookup bundle upgrade pathways", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract order data and find bundle upgrade paths
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const lineItems = data.line_items || [];
const email = data.email || data.customer?.email || "";
const name = data.customer?.first_name || data.billing_address?.first_name || "there";
const phone = data.customer?.phone || data.billing_address?.phone || "";
// Only proceed if single item
if (lineItems.length !== 1) {
  return { is_single_item: "no", customer_email: email, customer_name: name };
}
const item = lineItems[0];
const productTitle = item.title || item.name || "";
const productPrice = parseFloat(item.price) || 0;
const productCategory = item.product_type || item.vendor || "";
const productId = item.product_id || "";
const orderId = data.id || data.order_number || "";
const orderTotal = parseFloat(data.total_price) || productPrice;
// Define bundle pathways based on category
const bundleMap = {
  "skincare": ["cleanser", "serum", "moisturizer", "sunscreen"],
  "electronics": ["device", "case", "charger", "screen_protector"],
  "fitness": ["equipment", "mat", "bands", "water_bottle"],
  "kitchen": ["appliance", "utensils", "storage", "cleaning"],
};
const categoryKey = Object.keys(bundleMap).find(k => productCategory.toLowerCase().includes(k)) || "";
const bundlePath = categoryKey ? bundleMap[categoryKey].join(", ") : "accessories, complementary items, care products";
const bundleDiscount = productPrice >= 100 ? 15 : 10;
const result = {
  is_single_item: "yes",
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  purchased_product: productTitle,
  purchased_price: productPrice,
  product_category: productCategory,
  product_id: productId,
  order_id: orderId,
  order_total: orderTotal,
  bundle_pathway: bundlePath,
  bundle_discount_pct: bundleDiscount,
  upgrade_window_hours: 48
};
return result;`,
          outputVariables: [
            { name: "is_single_item", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "purchased_product", type: "string" },
            { name: "purchased_price", type: "number" },
            { name: "product_category", type: "string" },
            { name: "product_id", type: "string" },
            { name: "order_id", type: "string" },
            { name: "order_total", type: "number" },
            { name: "bundle_pathway", type: "string" },
            { name: "bundle_discount_pct", type: "number" },
            { name: "upgrade_window_hours", type: "number" },
          ],
        },
      }),

      // 3. AI upgrade pitch
      aiNode(
        "3",
        "AI generate bundle upgrade pitch",
        520,
        "You are an e-commerce upsell specialist. The customer just bought a single item. Write a compelling post-purchase email pitching a bundle upgrade. Show the purchased item alongside 2-3 complementary items. Emphasize how the items work together, the bundle discount, and that they can add on to their existing order within a limited window. Respond JSON: {email_subject: string, email_body: string, bundle_item_1: string, bundle_item_2: string, upgrade_cta: string, value_proposition: string}",
        "Customer: {customer_name} | Purchased: {purchased_product} (${purchased_price}) | Category: {product_category} | Bundle path: {bundle_pathway} | Discount: {bundle_discount_pct}% | Window: {upgrade_window_hours}h | Order: #{order_id}",
        [
          { field: "email_subject", as: "upgrade_subject", type: "string" },
          { field: "email_body", as: "upgrade_body", type: "string" },
          { field: "bundle_item_1", as: "bundle_item_1", type: "string" },
          { field: "bundle_item_2", as: "bundle_item_2", type: "string" },
          { field: "upgrade_cta", as: "upgrade_cta", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
        ],
      ),

      // 4. Side-by-side email
      emailNode(
        "4",
        "Send bundle upgrade comparison email",
        760,
        "{customer_email}",
        "{upgrade_subject}",
        "<p>Hi {customer_name},</p><p>Great choice on {purchased_product}!</p><p>{upgrade_body}</p><p><strong>Your bundle:</strong><br>{purchased_product} + {bundle_item_1} + {bundle_item_2}<br>Save {bundle_discount_pct}% when you add these within {upgrade_window_hours} hours.</p><p><a href='https://{shopify_store}.myshopify.com/cart?upgrade_order={order_id}'>{upgrade_cta}</a></p>",
      ),

      // 5. Upgrade conversion tracking
      sheetsNode(
        "5",
        "Track upgrade email conversions",
        1000,
        "BundleUpgradeClicks",
        '[["{customer_email}","{order_id}","{purchased_product}","{bundle_item_1}","{bundle_item_2}","{bundle_discount_pct}"]]',
      ),

      // 6. Revenue logging (Sheets)
      sheetsNode(
        "6",
        "Log revenue and upgrade opportunity",
        1240,
        "BundleUpgradeRevenue",
        '[["{customer_email}","{customer_name}","{order_id}","{purchased_product}","{purchased_price}","{product_category}","{bundle_pathway}","{bundle_discount_pct}","{value_proposition}","{upgrade_window_hours}"]]',
      ),

      // 7. Bundle performance
      slackNode(
        "7",
        "Slack: Bundle upgrade opportunity sent",
        1480,
        "Bundle upgrade email sent to {customer_name} ({customer_email})\\nOrder #{order_id}: {purchased_product} (${purchased_price})\\nBundle path: {bundle_pathway}\\nDiscount: {bundle_discount_pct}%\\nItems: {bundle_item_1}, {bundle_item_2}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 354. BUY-NOW-PAY-LATER REMINDER (8 nodes)
  // ===================================================================

  "Buy-Now-Pay-Later Reminder": {
    summary:
      "Recovers cart abandoners by promoting BNPL payment options. Triggers on cart abandonment, checks BNPL eligibility, calculates installment amounts via runCode, generates AI affordability-focused copy, sends a BNPL email, follows up with SMS, routes non-eligible shoppers to standard reminders, and logs BNPL conversion data to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer creates a checkout but does not complete the purchase within the configured abandonment window",
        setup:
          "In Shopify Settings -> Notifications -> Webhooks, add a checkouts/create webhook. The flow will check if payment was completed and only proceed on abandonment.",
        inputFields: [],
      }),

      // 2. BNPL eligibility check
      n("2", "runCode", "Extract cart + check BNPL eligibility", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract checkout data and determine BNPL eligibility
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const phone = data.billing_address?.phone || data.customer?.phone || "";
const cartTotal = parseFloat(data.total_price || data.subtotal_price || 0);
const lineItems = data.line_items || [];
const itemNames = lineItems.map(i => i.title || i.name).join(", ");
const itemCount = lineItems.length;
const currency = data.currency || "USD";
const abandonedUrl = data.abandoned_checkout_url || "";
// BNPL eligibility: typically $50-$10000 range
const BNPL_MIN = 50;
const BNPL_MAX = 10000;
const isBnplEligible = cartTotal >= BNPL_MIN && cartTotal <= BNPL_MAX;
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  cart_total: cartTotal,
  cart_items: itemNames,
  item_count: itemCount,
  currency: currency,
  checkout_url: abandonedUrl,
  is_bnpl_eligible: isBnplEligible ? "yes" : "no",
  bnpl_min: BNPL_MIN,
  bnpl_max: BNPL_MAX
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "item_count", type: "number" },
            { name: "currency", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "is_bnpl_eligible", type: "string" },
          ],
        },
      }),

      // 3. BNPL eligibility condition
      n("3", "condition", "BNPL eligible (cart in range)?", 520, {
        conditionData: [
          {
            variable: "is_bnpl_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Installment calculation (runCode)
      n("4", "runCode", "Calculate BNPL installment plans", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate installment options (Klarna/Afterpay/Affirm style)
const total = parseFloat(vars.cart_total) || 0;
// Pay in 4 (interest-free)
const payIn4 = Math.round(total / 4 * 100) / 100;
// Monthly (6 months, 0% APR promo)
const monthly6 = Math.round(total / 6 * 100) / 100;
// Monthly (12 months, 10% APR)
const apr = 0.10;
const monthlyRate = apr / 12;
const monthly12 = Math.round((total * monthlyRate * Math.pow(1 + monthlyRate, 12)) / (Math.pow(1 + monthlyRate, 12) - 1) * 100) / 100;
const result = {
  pay_in_4_amount: payIn4,
  pay_in_4_display: "4 payments of $" + payIn4.toFixed(2),
  monthly_6_amount: monthly6,
  monthly_6_display: "6 payments of $" + monthly6.toFixed(2) + " (0% APR)",
  monthly_12_amount: monthly12,
  monthly_12_display: "12 payments of $" + monthly12.toFixed(2) + " (10% APR)",
  recommended_plan: total <= 200 ? "pay_in_4" : total <= 500 ? "monthly_6" : "monthly_12",
  lowest_payment: "$" + Math.min(payIn4, monthly6, monthly12).toFixed(2) + "/mo"
};
return result;`,
          outputVariables: [
            { name: "pay_in_4_amount", type: "number" },
            { name: "pay_in_4_display", type: "string" },
            { name: "monthly_6_amount", type: "number" },
            { name: "monthly_6_display", type: "string" },
            { name: "monthly_12_amount", type: "number" },
            { name: "monthly_12_display", type: "string" },
            { name: "recommended_plan", type: "string" },
            { name: "lowest_payment", type: "string" },
          ],
        },
      }),

      // 5. AI affordability copy
      aiNode(
        "5",
        "AI generate BNPL recovery copy",
        1000,
        "You are a conversion copywriter specializing in BNPL messaging. Write a cart recovery email that leads with affordability and installment options. Make the total feel manageable by breaking it into payments. Include all three payment plan options. Tone: reassuring, helpful, not predatory. Respond JSON: {email_subject: string, email_body: string, sms_message: string, affordability_hook: string}",
        "Customer: {customer_name} | Cart: {cart_items} | Total: ${cart_total} | Pay in 4: {pay_in_4_display} | 6-month: {monthly_6_display} | 12-month: {monthly_12_display} | Recommended: {recommended_plan} | Lowest: {lowest_payment}",
        [
          { field: "email_subject", as: "bnpl_subject", type: "string" },
          { field: "email_body", as: "bnpl_body", type: "string" },
          { field: "sms_message", as: "bnpl_sms", type: "string" },
          {
            field: "affordability_hook",
            as: "affordability_hook",
            type: "string",
          },
        ],
      ),

      // 6. BNPL email
      emailNode(
        "6",
        "Send BNPL recovery email",
        1240,
        "{customer_email}",
        "{bnpl_subject}",
        "<p>Hi {customer_name},</p><p>{bnpl_body}</p><p><strong>Payment options for your ${cart_total} cart:</strong></p><ul><li>{pay_in_4_display}</li><li>{monthly_6_display}</li><li>{monthly_12_display}</li></ul><p>Starting from just {lowest_payment}</p><p><a href='{checkout_url}'>Complete Your Purchase</a></p>",
      ),

      // 7. SMS follow-up (Twilio) / Non-eligible standard reminder (converge at node 8)
      smsNode(
        "7",
        "SMS: BNPL payment reminder",
        1480,
        "{customer_phone}",
        "{bnpl_sms} Just {lowest_payment} for your ${cart_total} cart. Complete checkout: {checkout_url}",
      ),

      // 8. BNPL conversion logging (Sheets)
      sheetsNode(
        "8",
        "Log BNPL recovery attempt",
        1720,
        "BNPLRecovery",
        '[["{customer_email}","{customer_name}","{cart_total}","{cart_items}","{item_count}","{recommended_plan}","{lowest_payment}","{is_bnpl_eligible}","{affordability_hook}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // BNPL eligible -> calculate installments
      e("3", "8", "output_2"), // not eligible -> log as standard reminder
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 355. CANCELLATION RECEIVED CONFIRMATION (7 nodes)
  // ===================================================================

  "Cancellation Received Confirmation": {
    summary:
      "Sends professional cancellation confirmations with retention safeguards. Triggers on cancellation, pulls refund details from Stripe/Shopify, classifies the cancellation reason with AI, sends a confirmation email, checks customer LTV for high-value alerts, notifies the retention team on Slack, and tags the contact in HubSpot for win-back sequences.",
    nodes: [
      trigger("Webhook: Order/subscription cancellation", "webhook_app", {
        source: "Shopify / Stripe",
        event: "orders/cancelled",
        howItStarts:
          "A customer cancels an order or subscription, triggering the confirmation and retention workflow",
        setup:
          "In Shopify -> Webhooks, add an orders/cancelled endpoint. For subscriptions, add a customer.subscription.deleted webhook from Stripe. Both POST to the workflow URL.",
        inputFields: [],
      }),

      // 2. Refund detail pull (Stripe/Shopify)
      n("2", "hitApi", "Pull refund details from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/charges?customer={stripe_customer_id}&limit=5",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].amount",
              customName: "charge_amount_cents",
              type: "number",
            },
            {
              name: "data[0].currency",
              customName: "charge_currency",
              type: "string",
            },
            {
              name: "data[0].status",
              customName: "charge_status",
              type: "string",
            },
            {
              name: "data[0].refunded",
              customName: "is_refunded",
              type: "boolean",
            },
            {
              name: "data.length",
              customName: "total_charges",
              type: "number",
            },
          ],
        },
      }),

      // 3. Reason classification (AI)
      aiNode(
        "3",
        "AI classify cancellation reason",
        520,
        "You are a customer retention analyst. Read this cancellation event and classify the reason. Categories: price_sensitive, product_issue, competitor_switch, no_longer_needed, poor_experience, shipping_delay, wrong_item, other. Also extract: customer email, name, phone, order value, product(s), customer lifetime value if available, order count. Assess retention potential (high/medium/low). Respond JSON: {customer_email: string, customer_name: string, customer_phone: string, cancel_reason: string, reason_detail: string, order_value: number, products: string, lifetime_value: number, order_count: number, retention_potential: string, is_high_ltv: string}",
        "{webhook_payload} | Stripe charges: {total_charges} | Last charge: {charge_amount_cents} cents ({charge_currency}) | Refunded: {is_refunded}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "cancel_reason", as: "cancel_reason", type: "string" },
          { field: "reason_detail", as: "reason_detail", type: "string" },
          { field: "order_value", as: "order_value", type: "number" },
          { field: "products", as: "products", type: "string" },
          { field: "lifetime_value", as: "lifetime_value", type: "number" },
          { field: "order_count", as: "order_count", type: "number" },
          {
            field: "retention_potential",
            as: "retention_potential",
            type: "string",
          },
          { field: "is_high_ltv", as: "is_high_ltv", type: "string" },
        ],
      ),

      // 4. Confirmation email
      emailNode(
        "4",
        "Send cancellation confirmation email",
        760,
        "{customer_email}",
        "Your cancellation has been confirmed",
        "<p>Hi {customer_name},</p><p>We've received and processed your cancellation request.</p><p><strong>Details:</strong></p><ul><li>Products: {products}</li><li>Order value: ${order_value}</li><li>Refund status: {charge_status}</li></ul><p>If this was a mistake or you'd like to discuss alternatives, our team is here to help — just reply to this email.</p><p>We hope to see you again soon.</p>",
      ),

      // 5. High-LTV condition
      n("5", "condition", "High-LTV customer (retention priority)?", 1000, {
        conditionData: [
          {
            variable: "is_high_ltv",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Retention Slack alert
      slackNode(
        "6",
        "Slack: High-value cancellation alert",
        1240,
        "HIGH-VALUE CANCELLATION ALERT\\nCustomer: {customer_name} ({customer_email})\\nLTV: ${lifetime_value} | Orders: {order_count}\\nReason: {cancel_reason} — {reason_detail}\\nRetention potential: {retention_potential}\\nProducts: {products}\\nAction required: immediate outreach recommended",
      ),

      // 7. Win-back tagging (HubSpot)
      hubspotUpdateNode(
        "7",
        "Tag contact for win-back in HubSpot",
        1480,
        '{"cancel_reason":"{cancel_reason}","cancel_date":"{current_date}","retention_potential":"{retention_potential}","lifetime_value":"{lifetime_value}","winback_eligible":"true","winback_segment":"{cancel_reason}"}',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high LTV -> Slack alert
      e("5", "7", "output_2"), // standard LTV -> just tag for win-back
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 356. CARE INSTRUCTIONS FOR APPAREL OR HOME PRODUCTS (7 nodes)
  // ===================================================================

  "Care Instructions for Apparel or Home Products": {
    summary:
      "Sends personalized care instructions after product delivery. Triggers on delivery confirmation, maps products to materials via runCode, retrieves care content, generates an AI-personalized care guide, sends the care email, includes accessory/care product recommendations, and logs engagement to Sheets.",
    nodes: [
      trigger("Webhook: Order delivered", "webhook_app", {
        source: "Shopify / Shipping Carrier API",
        event: "fulfillments/update",
        howItStarts:
          "A customer's order is marked as delivered by the shipping carrier, triggering post-delivery care instructions",
        setup:
          "In Shopify -> Webhooks, add a fulfillments/update endpoint. The flow filters for delivered status and sends care instructions for applicable product categories.",
        inputFields: [],
      }),

      // 2. Product/material mapping (runCode)
      n("2", "runCode", "Map products to materials and care types", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract delivery data and map products to care instructions
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || data.destination?.email || "";
const name = data.destination?.first_name || data.customer?.first_name || "there";
const lineItems = data.line_items || [];
const productNames = lineItems.map(i => i.title || i.name).join(", ");
const productTypes = lineItems.map(i => i.product_type || "general");
// Material mapping
const materialMap = {
  "cotton": { wash: "Machine wash cold, gentle cycle", dry: "Tumble dry low or air dry", iron: "Medium heat", special: "Wash darks separately" },
  "silk": { wash: "Hand wash or dry clean only", dry: "Lay flat to dry", iron: "Low heat, inside out", special: "Avoid direct sunlight" },
  "wool": { wash: "Hand wash cold or dry clean", dry: "Reshape and lay flat", iron: "Steam only", special: "Store with cedar to prevent moths" },
  "leather": { wash: "Wipe with damp cloth", dry: "Air dry away from heat", iron: "Do not iron", special: "Condition every 3-6 months" },
  "linen": { wash: "Machine wash cold, gentle", dry: "Air dry preferred", iron: "High heat while slightly damp", special: "Wrinkles are natural and add character" },
  "ceramic": { wash: "Hand wash recommended", dry: "Air dry", iron: "N/A", special: "Avoid thermal shock — no sudden temperature changes" },
  "wood": { wash: "Wipe with damp cloth, dry immediately", dry: "N/A", iron: "N/A", special: "Oil every 6 months, avoid prolonged water contact" },
};
// Detect material from product type/name
const detectedMaterial = Object.keys(materialMap).find(m =>
  productNames.toLowerCase().includes(m) || productTypes.some(t => t.toLowerCase().includes(m))
) || "cotton";
const careInfo = materialMap[detectedMaterial];
const result = {
  customer_email: email,
  customer_name: name,
  product_names: productNames,
  detected_material: detectedMaterial,
  care_wash: careInfo.wash,
  care_dry: careInfo.dry,
  care_iron: careInfo.iron,
  care_special: careInfo.special,
  product_count: lineItems.length,
  is_apparel: productTypes.some(t => ["shirt","dress","pants","jacket","sweater","top","bottom","outerwear"].some(a => t.toLowerCase().includes(a))) ? "yes" : "no",
  is_home: productTypes.some(t => ["home","kitchen","bath","decor","furniture","bedding"].some(a => t.toLowerCase().includes(a))) ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "product_names", type: "string" },
            { name: "detected_material", type: "string" },
            { name: "care_wash", type: "string" },
            { name: "care_dry", type: "string" },
            { name: "care_iron", type: "string" },
            { name: "care_special", type: "string" },
            { name: "product_count", type: "number" },
            { name: "is_apparel", type: "string" },
            { name: "is_home", type: "string" },
          ],
        },
      }),

      // 3. Care content retrieval
      n("3", "hitApi", "Retrieve extended care content from CMS", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://cdn.yourstore.com/api/care-guides/{detected_material}.json",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "guide.title",
              customName: "care_guide_title",
              type: "string",
            },
            { name: "guide.tips", customName: "care_tips", type: "string" },
            {
              name: "guide.video_url",
              customName: "care_video_url",
              type: "string",
            },
            { name: "guide.faq", customName: "care_faq", type: "string" },
          ],
        },
      }),

      // 4. AI personalized guide
      aiNode(
        "4",
        "AI personalize care guide email",
        760,
        "You are a product care specialist. Write a personalized care instructions email for the customer's recently delivered products. Be warm and helpful. Include practical tips for their specific material type. If relevant, mention seasonal care tips. Respond JSON: {email_subject: string, email_body: string, quick_tips: string, seasonal_tip: string, accessory_suggestion: string}",
        "Customer: {customer_name} | Products: {product_names} | Material: {detected_material} | Wash: {care_wash} | Dry: {care_dry} | Iron: {care_iron} | Special: {care_special} | Guide: {care_guide_title} | Tips: {care_tips} | Is apparel: {is_apparel} | Is home: {is_home}",
        [
          { field: "email_subject", as: "care_subject", type: "string" },
          { field: "email_body", as: "care_body", type: "string" },
          { field: "quick_tips", as: "quick_tips", type: "string" },
          { field: "seasonal_tip", as: "seasonal_tip", type: "string" },
          {
            field: "accessory_suggestion",
            as: "accessory_suggestion",
            type: "string",
          },
        ],
      ),

      // 5. Care email
      emailNode(
        "5",
        "Send personalized care instructions",
        1000,
        "{customer_email}",
        "{care_subject}",
        "<p>Hi {customer_name},</p><p>{care_body}</p><p><strong>Quick Care Guide for {detected_material}:</strong></p><ul><li>Washing: {care_wash}</li><li>Drying: {care_dry}</li><li>Ironing: {care_iron}</li><li>Special: {care_special}</li></ul><p>{quick_tips}</p><p><em>{seasonal_tip}</em></p>",
      ),

      // 6. Accessory recommendations
      emailNode(
        "6",
        "Send care accessory recommendations",
        1240,
        "{customer_email}",
        "Keep your {product_names} looking new",
        "<p>Hi {customer_name},</p><p>{accessory_suggestion}</p><p>Browse our care accessories collection to extend the life of your purchase.</p>",
        460,
      ),

      // 7. Engagement logging (Sheets)
      sheetsNode(
        "7",
        "Log care instruction engagement",
        1480,
        "CareInstructions",
        '[["{customer_email}","{customer_name}","{product_names}","{detected_material}","{care_wash}","{care_dry}","{is_apparel}","{is_home}","{accessory_suggestion}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 357. CART RECOVERY EXCLUDING PURCHASERS VIA EXCEPTION EVENT (8 nodes)
  // ===================================================================

  "Cart Recovery Excluding Purchasers via Exception Event": {
    summary:
      "Recovers abandoned carts while dynamically suppressing customers who complete purchase between abandon and recovery. Triggers on cart abandonment, immediately checks for purchase, waits a short buffer, re-checks purchase status, sends recovery email only to non-purchasers, falls back to SMS, logs suppression events, and audits flow accuracy to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer creates a checkout but does not complete payment within the abandonment window",
        setup:
          "In Shopify -> Webhooks, add a checkouts/create endpoint. The flow monitors for purchase completion between abandonment and recovery send.",
        inputFields: [],
      }),

      // 2. Immediate purchase check condition
      n("2", "runCode", "Extract cart data + initial purchase check", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract checkout data for recovery
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const phone = data.billing_address?.phone || data.customer?.phone || "";
const cartTotal = parseFloat(data.total_price || 0);
const lineItems = data.line_items || [];
const itemNames = lineItems.map(i => i.title || i.name).join(", ");
const checkoutToken = data.token || data.id || "";
const abandonedUrl = data.abandoned_checkout_url || "";
// Check if already completed (some platforms include this)
const completedAt = data.completed_at || data.closed_at || null;
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  cart_total: cartTotal,
  cart_items: itemNames,
  item_count: lineItems.length,
  checkout_token: checkoutToken,
  checkout_url: abandonedUrl,
  has_purchased: completedAt ? "yes" : "no",
  check_timestamp: new Date().toISOString()
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "item_count", type: "number" },
            { name: "checkout_token", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "has_purchased", type: "string" },
            { name: "check_timestamp", type: "string" },
          ],
        },
      }),

      // 3. First purchase check condition
      n("3", "condition", "Already purchased?", 520, {
        conditionData: [
          {
            variable: "has_purchased",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Short buffer delay
      n("4", "delayAction", "Wait 1 hour before recovery", 760, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 1,
          delayUnit: "hours",
        },
      }),

      // 5. Second purchase check (re-query Shopify)
      shopifyNode(
        "5",
        "Re-check checkout completion status",
        1000,
        "GET",
        "checkouts/{checkout_token}.json",
        [],
        [
          {
            name: "checkout.completed_at",
            customName: "completed_at_recheck",
            type: "string",
          },
          {
            name: "checkout.order_id",
            customName: "order_id_recheck",
            type: "string",
          },
        ],
      ),

      // 6. Recovery email (only if still not purchased)
      emailNode(
        "6",
        "Send cart recovery email",
        1240,
        "{customer_email}",
        "You left something behind, {customer_name}",
        "<p>Hi {customer_name},</p><p>You were so close! Your cart with {cart_items} (${cart_total}) is still waiting for you.</p><p><a href='{checkout_url}'>Complete Your Purchase</a></p><p>Need help? Just reply to this email.</p>",
      ),

      // 7. SMS fallback (Twilio)
      smsNode(
        "7",
        "SMS fallback: Cart recovery",
        1480,
        "{customer_phone}",
        "Hi {customer_name}, your ${cart_total} cart is still saved! Complete checkout: {checkout_url}",
      ),

      // 8. Flow accuracy audit (Sheets)
      sheetsNode(
        "8",
        "Log suppression + accuracy audit",
        1720,
        "CartRecoveryAudit",
        '[["{customer_email}","{customer_name}","{cart_total}","{cart_items}","{has_purchased}","{completed_at_recheck}","{order_id_recheck}","{check_timestamp}","{checkout_token}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // not purchased -> delay
      e("3", "8", "output_2"), // already purchased -> suppress, log only
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 358. CART RECOVERY SPLIT BY CATEGORY (8 nodes)
  // ===================================================================

  "Cart Recovery Split by Category": {
    summary:
      "Routes cart recovery emails based on the dominant product category. Triggers on abandonment, detects the dominant category via runCode, routes by category condition, sends category-specific emails for apparel (sizing), electronics (specs), beauty (ingredients), and home (dimensions), and falls back to a generic recovery with logging.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer abandons a cart, and the recovery email is tailored to the dominant product category",
        setup:
          "In Shopify -> Webhooks, add a checkouts/create endpoint pointing to the workflow URL.",
        inputFields: [],
      }),

      // 2. Dominant category detection (runCode)
      n("2", "runCode", "Detect dominant cart category", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse cart and determine dominant category
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const phone = data.billing_address?.phone || "";
const cartTotal = parseFloat(data.total_price || 0);
const lineItems = data.line_items || [];
const itemNames = lineItems.map(i => i.title || i.name).join(", ");
const checkoutUrl = data.abandoned_checkout_url || "";
// Count items by category
const catCounts = {};
lineItems.forEach(item => {
  const cat = (item.product_type || item.vendor || "general").toLowerCase();
  let bucket = "general";
  if (["shirt","dress","pants","jacket","shoes","sweater","top","bottom","skirt","jeans"].some(k => cat.includes(k) || (item.title||"").toLowerCase().includes(k))) bucket = "apparel";
  else if (["phone","laptop","tablet","camera","headphones","speaker","tv","monitor","electronic"].some(k => cat.includes(k) || (item.title||"").toLowerCase().includes(k))) bucket = "electronics";
  else if (["skincare","makeup","serum","cleanser","moisturizer","beauty","cosmetic","fragrance"].some(k => cat.includes(k) || (item.title||"").toLowerCase().includes(k))) bucket = "beauty";
  else if (["furniture","decor","bedding","kitchen","bath","rug","lamp","curtain","home"].some(k => cat.includes(k) || (item.title||"").toLowerCase().includes(k))) bucket = "home";
  catCounts[bucket] = (catCounts[bucket] || 0) + 1;
});
const dominant = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0];
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  cart_total: cartTotal,
  cart_items: itemNames,
  checkout_url: checkoutUrl,
  dominant_category: dominant ? dominant[0] : "general",
  category_item_count: dominant ? dominant[1] : 0,
  total_items: lineItems.length
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "dominant_category", type: "string" },
            { name: "category_item_count", type: "number" },
            { name: "total_items", type: "number" },
          ],
        },
      }),

      // 3. Category routing condition
      n("3", "condition", "Apparel category dominant?", 520, {
        conditionData: [
          {
            variable: "dominant_category",
            value: "apparel",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Apparel sizing email
      emailNode(
        "4",
        "Send apparel recovery with sizing help",
        760,
        "{customer_email}",
        "Not sure about the fit, {customer_name}?",
        "<p>Hi {customer_name},</p><p>We noticed you left {cart_items} in your cart. Not sure about sizing? Check our detailed size guide to find your perfect fit.</p><p>Cart total: ${cart_total}</p><p><a href='{checkout_url}'>Complete Your Order</a> | <a href='https://{shopify_store}.myshopify.com/pages/size-guide'>View Size Guide</a></p>",
        460,
      ),

      // 5. Electronics specs email
      emailNode(
        "5",
        "Send electronics recovery with specs",
        760,
        "{customer_email}",
        "Still comparing specs, {customer_name}?",
        "<p>Hi {customer_name},</p><p>You were looking at {cart_items}. Need help comparing specs or compatibility? Our tech team is here to help.</p><p>Cart total: ${cart_total}</p><p><a href='{checkout_url}'>Complete Your Order</a></p>",
        840,
      ),

      // 6. Beauty ingredients email
      emailNode(
        "6",
        "Send beauty recovery with ingredient info",
        760,
        "{customer_email}",
        "Questions about ingredients, {customer_name}?",
        "<p>Hi {customer_name},</p><p>You left {cart_items} in your cart. Want to learn more about the ingredients and how they'll work for your skin type?</p><p>Cart total: ${cart_total}</p><p><a href='{checkout_url}'>Complete Your Order</a> | <a href='https://{shopify_store}.myshopify.com/pages/ingredient-glossary'>Ingredient Guide</a></p>",
        1220,
      ),

      // 7. Home dimensions email
      emailNode(
        "7",
        "Send home recovery with dimensions",
        760,
        "{customer_email}",
        "Need to double-check measurements, {customer_name}?",
        "<p>Hi {customer_name},</p><p>You left {cart_items} in your cart. Unsure about dimensions or how it'll look in your space? Use our AR room viewer or check detailed measurements.</p><p>Cart total: ${cart_total}</p><p><a href='{checkout_url}'>Complete Your Order</a></p>",
        1600,
      ),

      // 8. Generic fallback + logging
      sheetsNode(
        "8",
        "Log category-split recovery",
        1000,
        "CartRecoveryByCategory",
        '[["{customer_email}","{customer_name}","{cart_total}","{cart_items}","{dominant_category}","{category_item_count}","{total_items}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // apparel -> sizing email
      e("3", "5", "output_2"), // not apparel -> check other categories (electronics as next)
      e("4", "8"),
      e("5", "8"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 359. CART RECOVERY SPLIT BY DISCOUNT ELIGIBILITY (8 nodes)
  // ===================================================================

  "Cart Recovery Split by Discount Eligibility": {
    summary:
      "Routes cart recovery by whether items are eligible for discounts, protecting margins on restricted products. Triggers on abandonment, checks discountability rules via runCode, routes by eligibility condition, sends coupon-based recovery for eligible carts, value/urgency messaging for non-eligible, hybrid path for mixed carts, sends the recovery email, and logs margin data to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer abandons a cart — the recovery strategy depends on whether the products are discount-eligible",
        setup:
          "In Shopify -> Webhooks, add a checkouts/create endpoint. Connect your discount rules engine or product metadata for eligibility checks.",
        inputFields: [],
      }),

      // 2. Discountability rules check (runCode)
      n("2", "runCode", "Check discount eligibility rules", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine if cart items are discount-eligible
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const phone = data.billing_address?.phone || "";
const cartTotal = parseFloat(data.total_price || 0);
const lineItems = data.line_items || [];
const itemNames = lineItems.map(i => i.title || i.name).join(", ");
const checkoutUrl = data.abandoned_checkout_url || "";
// Check each item for discount restrictions
const NON_DISCOUNTABLE_TAGS = ["new-arrival", "limited-edition", "map-pricing", "clearance", "already-discounted"];
let eligibleTotal = 0;
let nonEligibleTotal = 0;
lineItems.forEach(item => {
  const tags = (item.tags || "").toLowerCase().split(",").map(t => t.trim());
  const isRestricted = NON_DISCOUNTABLE_TAGS.some(t => tags.includes(t));
  const price = parseFloat(item.price || 0) * (item.quantity || 1);
  if (isRestricted) { nonEligibleTotal += price; }
  else { eligibleTotal += price; }
});
const eligiblePct = cartTotal > 0 ? Math.round((eligibleTotal / cartTotal) * 100) : 0;
let discountPath;
if (eligiblePct >= 80) discountPath = "full_discount";
else if (eligiblePct >= 30) discountPath = "mixed";
else discountPath = "no_discount";
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  cart_total: cartTotal,
  cart_items: itemNames,
  checkout_url: checkoutUrl,
  eligible_total: eligibleTotal,
  non_eligible_total: nonEligibleTotal,
  eligible_pct: eligiblePct,
  discount_path: discountPath,
  coupon_code: discountPath !== "no_discount" ? "SAVE10_" + Date.now().toString(36).toUpperCase() : "",
  discount_amount: discountPath !== "no_discount" ? Math.round(eligibleTotal * 0.10 * 100) / 100 : 0
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "eligible_total", type: "number" },
            { name: "non_eligible_total", type: "number" },
            { name: "eligible_pct", type: "number" },
            { name: "discount_path", type: "string" },
            { name: "coupon_code", type: "string" },
            { name: "discount_amount", type: "number" },
          ],
        },
      }),

      // 3. Eligibility condition
      n("3", "condition", "Fully discount-eligible (>80%)?", 520, {
        conditionData: [
          {
            variable: "discount_path",
            value: "full_discount",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Discount path with coupon
      emailNode(
        "4",
        "Send discount recovery email",
        760,
        "{customer_email}",
        "Here's 10% off your cart, {customer_name}",
        "<p>Hi {customer_name},</p><p>You left {cart_items} in your cart (${cart_total}). Use code <strong>{coupon_code}</strong> for 10% off — saving you ${discount_amount}.</p><p><a href='{checkout_url}?discount={coupon_code}'>Apply Discount & Checkout</a></p>",
        460,
      ),

      // 5. Value/urgency path (no discount)
      emailNode(
        "5",
        "Send value recovery email (no discount)",
        760,
        "{customer_email}",
        "Your {cart_items} are waiting, {customer_name}",
        "<p>Hi {customer_name},</p><p>You left some great items in your cart (${cart_total}). These products are in high demand — don't miss out on {cart_items}.</p><p><a href='{checkout_url}'>Complete Your Order</a></p>",
        840,
      ),

      // 6. Mixed hybrid path
      emailNode(
        "6",
        "Send hybrid recovery email",
        760,
        "{customer_email}",
        "Save on select items in your cart, {customer_name}",
        "<p>Hi {customer_name},</p><p>Good news — some items in your ${cart_total} cart qualify for a discount! Use code <strong>{coupon_code}</strong> to save ${discount_amount} on eligible items.</p><p><a href='{checkout_url}?discount={coupon_code}'>Checkout with Savings</a></p>",
        1220,
      ),

      // 7. Recovery email (AI-enhanced, applies to all paths)
      aiNode(
        "7",
        "AI generate path-specific recovery copy",
        1000,
        "You are a cart recovery specialist. Based on the customer's discount eligibility path, write a compelling follow-up SMS. For discount paths, emphasize savings. For value paths, emphasize product quality and scarcity. For mixed, acknowledge partial savings. Respond JSON: {sms_text: string, recovery_strategy: string}",
        "Customer: {customer_name} | Cart: {cart_items} (${cart_total}) | Path: {discount_path} | Eligible: {eligible_pct}% | Coupon: {coupon_code} | Savings: ${discount_amount}",
        [
          { field: "sms_text", as: "recovery_sms", type: "string" },
          {
            field: "recovery_strategy",
            as: "recovery_strategy",
            type: "string",
          },
        ],
      ),

      // 8. Margin logging (Sheets)
      sheetsNode(
        "8",
        "Log discount eligibility + margin data",
        1240,
        "CartRecoveryDiscountSplit",
        '[["{customer_email}","{customer_name}","{cart_total}","{eligible_total}","{non_eligible_total}","{eligible_pct}","{discount_path}","{coupon_code}","{discount_amount}","{recovery_strategy}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // fully eligible -> discount email
      e("3", "5", "output_2"), // not eligible -> value/urgency email
      e("4", "7"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 360. CART RECOVERY SPLIT BY LOYALTY STATUS (8 nodes)
  // ===================================================================

  "Cart Recovery Split by Loyalty Status": {
    summary:
      "Tailors cart recovery based on the customer's loyalty tier. Triggers on abandonment, matches the loyalty profile, routes by tier condition, sends non-member acquisition emails, standard member points emails, VIP concierge emails, follows up with SMS for non-openers, and logs loyalty segment performance to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer abandons their cart — recovery messaging is tailored to their loyalty program status",
        setup:
          "In Shopify -> Webhooks, add a checkouts/create endpoint. Ensure your loyalty platform (Smile.io, LoyaltyLion, Yotpo) data is accessible via the customer record.",
        inputFields: [],
      }),

      // 2. Loyalty profile match
      n("2", "runCode", "Extract cart + match loyalty tier", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract checkout data and determine loyalty tier
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const phone = data.billing_address?.phone || "";
const cartTotal = parseFloat(data.total_price || 0);
const lineItems = data.line_items || [];
const itemNames = lineItems.map(i => i.title || i.name).join(", ");
const checkoutUrl = data.abandoned_checkout_url || "";
// Loyalty data from customer metafields or tags
const customerTags = (data.customer?.tags || "").toLowerCase();
const loyaltyPoints = parseInt(data.customer?.metafields?.loyalty_points || 0);
const totalSpent = parseFloat(data.customer?.total_spent || 0);
const orderCount = parseInt(data.customer?.orders_count || 0);
let loyaltyTier;
if (customerTags.includes("vip") || totalSpent >= 1000 || orderCount >= 10) loyaltyTier = "vip";
else if (orderCount >= 1 || loyaltyPoints > 0) loyaltyTier = "member";
else loyaltyTier = "non_member";
// Calculate points they'd earn
const pointsEarnable = Math.round(cartTotal * 2); // 2 points per dollar
const pointsValue = Math.round(pointsEarnable / 100 * 100) / 100; // $1 per 100 points
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  cart_total: cartTotal,
  cart_items: itemNames,
  checkout_url: checkoutUrl,
  loyalty_tier: loyaltyTier,
  loyalty_points: loyaltyPoints,
  total_spent: totalSpent,
  order_count: orderCount,
  points_earnable: pointsEarnable,
  points_value: pointsValue
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "loyalty_tier", type: "string" },
            { name: "loyalty_points", type: "number" },
            { name: "total_spent", type: "number" },
            { name: "order_count", type: "number" },
            { name: "points_earnable", type: "number" },
            { name: "points_value", type: "number" },
          ],
        },
      }),

      // 3. Tier routing condition
      n("3", "condition", "VIP loyalty member?", 520, {
        conditionData: [
          {
            variable: "loyalty_tier",
            value: "vip",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Non-member acquisition email
      emailNode(
        "4",
        "Send non-member loyalty acquisition email",
        760,
        "{customer_email}",
        "Complete your cart and earn {points_earnable} rewards points, {customer_name}",
        "<p>Hi {customer_name},</p><p>Your cart ({cart_items}, ${cart_total}) is waiting! Did you know you could earn <strong>{points_earnable} reward points</strong> (worth ${points_value}) on this purchase?</p><p>Join our loyalty program for free when you checkout.</p><p><a href='{checkout_url}'>Complete & Earn Points</a></p>",
        460,
      ),

      // 5. Standard points email
      emailNode(
        "5",
        "Send member points reminder email",
        760,
        "{customer_email}",
        "Earn {points_earnable} more points on your cart, {customer_name}",
        "<p>Hi {customer_name},</p><p>You have <strong>{loyalty_points} points</strong> in your rewards account! Complete your ${cart_total} cart to earn <strong>{points_earnable} more points</strong>.</p><p>Items: {cart_items}</p><p><a href='{checkout_url}'>Checkout & Earn Points</a></p>",
        840,
      ),

      // 6. VIP concierge email
      emailNode(
        "6",
        "Send VIP concierge recovery email",
        760,
        "{customer_email}",
        "Your personal shopping assistant here, {customer_name}",
        "<p>Hi {customer_name},</p><p>As a VIP member with {loyalty_points} points, you have access to exclusive benefits. I noticed your cart with {cart_items} (${cart_total}) is waiting.</p><p>As your personal shopping assistant, I can help with sizing, styling, or any questions. You'll also earn <strong>{points_earnable} points</strong> on this purchase.</p><p><a href='{checkout_url}'>Complete Your VIP Order</a></p><p>Or reply to this email for personalized assistance.</p>",
        1220,
      ),

      // 7. SMS for non-openers (Twilio)
      smsNode(
        "7",
        "SMS: Loyalty-aware cart reminder",
        1000,
        "{customer_phone}",
        "Hi {customer_name}, your ${cart_total} cart earns you {points_earnable} reward points! Complete checkout: {checkout_url}",
      ),

      // 8. Loyalty segment logging (Sheets)
      sheetsNode(
        "8",
        "Log loyalty segment recovery data",
        1240,
        "CartRecoveryLoyalty",
        '[["{customer_email}","{customer_name}","{cart_total}","{cart_items}","{loyalty_tier}","{loyalty_points}","{points_earnable}","{points_value}","{total_spent}","{order_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "6", "output_1"), // VIP -> concierge email
      e("3", "4", "output_2"), // not VIP -> non-member or member path
      e("4", "7"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 361. CART RECOVERY SPLIT BY MARGIN (8 nodes)
  // ===================================================================

  "Cart Recovery Split by Margin": {
    summary:
      "Adjusts cart recovery aggressiveness based on product margin. Triggers on abandonment, calculates COGS and margin via runCode, routes by margin bucket, sends high-margin carts a generous discount, medium-margin a value pitch, low-margin an urgency-only message, alerts merchandising on Slack for margin spikes, and logs profit data to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer abandons a cart — recovery aggressiveness is calibrated to the cart's profit margin",
        setup:
          "In Shopify -> Webhooks, add a checkouts/create endpoint. Ensure product cost (COGS) data is available via metafields or inventory cost fields.",
        inputFields: [],
      }),

      // 2. COGS/margin calculation (runCode)
      n("2", "runCode", "Calculate cart COGS and margin", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate margin for cart items to determine recovery strategy
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const phone = data.billing_address?.phone || "";
const cartTotal = parseFloat(data.total_price || 0);
const lineItems = data.line_items || [];
const itemNames = lineItems.map(i => i.title || i.name).join(", ");
const checkoutUrl = data.abandoned_checkout_url || "";
// Calculate COGS (from metafields or cost_per_item)
let totalCogs = 0;
lineItems.forEach(item => {
  const cost = parseFloat(item.cost_per_item || item.compare_at_price || 0) || parseFloat(item.price || 0) * 0.45;
  totalCogs += cost * (item.quantity || 1);
});
const grossMargin = cartTotal > 0 ? Math.round(((cartTotal - totalCogs) / cartTotal) * 100) : 0;
const grossProfit = Math.round((cartTotal - totalCogs) * 100) / 100;
let marginBucket;
if (grossMargin >= 60) marginBucket = "high";
else if (grossMargin >= 35) marginBucket = "medium";
else marginBucket = "low";
// Max discount that preserves minimum 20% margin
const maxDiscountPct = Math.max(0, Math.round(grossMargin - 20));
const maxDiscountAmount = Math.round(cartTotal * maxDiscountPct / 100 * 100) / 100;
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  cart_total: cartTotal,
  cart_items: itemNames,
  checkout_url: checkoutUrl,
  total_cogs: totalCogs,
  gross_margin_pct: grossMargin,
  gross_profit: grossProfit,
  margin_bucket: marginBucket,
  max_discount_pct: maxDiscountPct,
  max_discount_amount: maxDiscountAmount,
  coupon_code: marginBucket === "high" ? "SAVE" + maxDiscountPct + "_" + Date.now().toString(36).toUpperCase() : ""
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "total_cogs", type: "number" },
            { name: "gross_margin_pct", type: "number" },
            { name: "gross_profit", type: "number" },
            { name: "margin_bucket", type: "string" },
            { name: "max_discount_pct", type: "number" },
            { name: "max_discount_amount", type: "number" },
            { name: "coupon_code", type: "string" },
          ],
        },
      }),

      // 3. Margin bucket condition
      n("3", "condition", "High-margin cart (>60%)?", 520, {
        conditionData: [
          {
            variable: "margin_bucket",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. High-margin discount email
      emailNode(
        "4",
        "Send high-margin generous discount email",
        760,
        "{customer_email}",
        "Exclusive {max_discount_pct}% off your cart, {customer_name}",
        "<p>Hi {customer_name},</p><p>We'd love to see you complete your order! Here's an exclusive <strong>{max_discount_pct}% off</strong> your ${cart_total} cart.</p><p>Items: {cart_items}</p><p>Use code <strong>{coupon_code}</strong> to save up to ${max_discount_amount}.</p><p><a href='{checkout_url}?discount={coupon_code}'>Apply Discount & Checkout</a></p>",
        460,
      ),

      // 5. Medium-margin value email
      emailNode(
        "5",
        "Send medium-margin value recovery email",
        760,
        "{customer_email}",
        "Your cart is still waiting, {customer_name}",
        "<p>Hi {customer_name},</p><p>You left some great items in your cart (${cart_total}). Here's why customers love these products:</p><p>Items: {cart_items}</p><p>Free shipping on orders over $50. Free returns within 30 days.</p><p><a href='{checkout_url}'>Complete Your Order</a></p>",
        840,
      ),

      // 6. Low-margin urgency email
      emailNode(
        "6",
        "Send low-margin urgency-only email",
        760,
        "{customer_email}",
        "Hurry — {cart_items} are selling fast, {customer_name}",
        "<p>Hi {customer_name},</p><p>Your cart items ({cart_items}, ${cart_total}) are in high demand! These popular items may sell out soon.</p><p><a href='{checkout_url}'>Secure Your Items Now</a></p>",
        1220,
      ),

      // 7. Merchandising spike alert (Slack)
      slackNode(
        "7",
        "Slack: Margin-aware recovery alert",
        1000,
        "Cart recovery sent — Margin analysis:\\nCustomer: {customer_name} ({customer_email})\\nCart: ${cart_total} | COGS: ${total_cogs} | Margin: {gross_margin_pct}%\\nBucket: {margin_bucket} | Max discount: {max_discount_pct}%\\nCoupon: {coupon_code}",
      ),

      // 8. Profit logging (Sheets)
      sheetsNode(
        "8",
        "Log margin-based recovery profit data",
        1240,
        "CartRecoveryMargin",
        '[["{customer_email}","{customer_name}","{cart_total}","{total_cogs}","{gross_margin_pct}","{gross_profit}","{margin_bucket}","{max_discount_pct}","{max_discount_amount}","{coupon_code}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // high margin -> generous discount
      e("3", "5", "output_2"), // not high -> medium (value) or low (urgency)
      e("4", "7"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 362. CART RECOVERY SPLIT BY SHIPPING SPEED (7 nodes)
  // ===================================================================

  "Cart Recovery Split by Shipping Speed": {
    summary:
      "Recovers abandoned carts by highlighting shipping advantages. Triggers on abandonment, calculates shipping options via runCode, routes by speed condition, sends fast-shipping urgency emails, free-shipping threshold nudges, or standard shipping emails, and includes add-on suggestions with logging.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer abandons a cart — recovery messaging highlights relevant shipping speed and cost advantages",
        setup:
          "In Shopify -> Webhooks, add a checkouts/create endpoint. Configure shipping zone data for accurate speed/cost calculations.",
        inputFields: [],
      }),

      // 2. Shipping calculation (runCode)
      n("2", "runCode", "Calculate shipping speed and options", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine shipping options and speeds for recovery messaging
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const phone = data.billing_address?.phone || "";
const cartTotal = parseFloat(data.total_price || 0);
const lineItems = data.line_items || [];
const itemNames = lineItems.map(i => i.title || i.name).join(", ");
const checkoutUrl = data.abandoned_checkout_url || "";
const shippingZone = data.shipping_address?.province_code || data.shipping_address?.country_code || "US";
// Calculate shipping options
const FREE_SHIPPING_THRESHOLD = 75;
const amountToFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - cartTotal);
const qualifiesForFree = cartTotal >= FREE_SHIPPING_THRESHOLD;
// Estimate delivery days by zone
const isLocal = ["CA", "NY", "TX", "FL"].includes(shippingZone);
const fastDays = isLocal ? 1 : 2;
const standardDays = isLocal ? 3 : 5;
let shippingRoute;
if (isLocal && qualifiesForFree) shippingRoute = "fast_free";
else if (qualifiesForFree) shippingRoute = "standard_free";
else if (amountToFreeShipping <= 25) shippingRoute = "near_threshold";
else shippingRoute = "standard_paid";
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  cart_total: cartTotal,
  cart_items: itemNames,
  checkout_url: checkoutUrl,
  shipping_route: shippingRoute,
  free_shipping_threshold: FREE_SHIPPING_THRESHOLD,
  amount_to_free: amountToFreeShipping,
  qualifies_free: qualifiesForFree ? "yes" : "no",
  fast_delivery_days: fastDays,
  standard_delivery_days: standardDays,
  shipping_zone: shippingZone
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "shipping_route", type: "string" },
            { name: "free_shipping_threshold", type: "number" },
            { name: "amount_to_free", type: "number" },
            { name: "qualifies_free", type: "string" },
            { name: "fast_delivery_days", type: "number" },
            { name: "standard_delivery_days", type: "number" },
            { name: "shipping_zone", type: "string" },
          ],
        },
      }),

      // 3. Speed routing condition
      n("3", "condition", "Fast + free shipping available?", 520, {
        conditionData: [
          {
            variable: "shipping_route",
            value: "fast_free",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Fast-shipping email
      emailNode(
        "4",
        "Send fast-shipping urgency email",
        760,
        "{customer_email}",
        "Get {cart_items} in {fast_delivery_days} day(s) with FREE shipping, {customer_name}!",
        "<p>Hi {customer_name},</p><p>Great news — your cart qualifies for <strong>FREE {fast_delivery_days}-day shipping</strong>!</p><p>Items: {cart_items} (${cart_total})</p><p>Order today and get it fast.</p><p><a href='{checkout_url}'>Get It in {fast_delivery_days} Day(s)</a></p>",
        460,
      ),

      // 5. Free-shipping threshold email
      emailNode(
        "5",
        "Send free-shipping threshold nudge",
        760,
        "{customer_email}",
        "You're ${amount_to_free} away from FREE shipping, {customer_name}!",
        "<p>Hi {customer_name},</p><p>Your cart is ${cart_total} — just <strong>${amount_to_free} more</strong> unlocks FREE shipping! Add a small item to save on delivery.</p><p>Items: {cart_items}</p><p><a href='{checkout_url}'>Add & Save on Shipping</a></p>",
        840,
      ),

      // 6. Standard email
      emailNode(
        "6",
        "Send standard shipping recovery email",
        760,
        "{customer_email}",
        "Your cart is waiting, {customer_name}",
        "<p>Hi {customer_name},</p><p>You left {cart_items} (${cart_total}) in your cart. Estimated delivery: {standard_delivery_days} business days.</p><p><a href='{checkout_url}'>Complete Your Order</a></p>",
        1220,
      ),

      // 7. Add-on suggestion + logging
      sheetsNode(
        "7",
        "Log shipping-split recovery + add-on data",
        1000,
        "CartRecoveryShipping",
        '[["{customer_email}","{customer_name}","{cart_total}","{cart_items}","{shipping_route}","{qualifies_free}","{amount_to_free}","{fast_delivery_days}","{standard_delivery_days}","{shipping_zone}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // fast + free -> urgency email
      e("3", "5", "output_2"), // not fast+free -> threshold nudge or standard
      e("4", "7"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 363. CART RECOVERY SPLIT BY STOCK RISK (8 nodes)
  // ===================================================================

  "Cart Recovery Split by Stock Risk": {
    summary:
      "Adjusts cart recovery urgency based on real-time stock levels. Triggers on abandonment, checks live stock on Shopify, routes by stock condition, sends low-stock urgency emails, OOS substitute/waitlist offers, standard recovery for healthy stock, SMS for critically low items, and logs stock-vs-conversion data to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer abandons a cart — the recovery urgency is calibrated to real-time inventory levels of carted items",
        setup:
          "In Shopify -> Webhooks, add a checkouts/create endpoint. The flow checks live inventory via the Shopify Inventory API.",
        inputFields: [],
      }),

      // 2. Live stock check (Shopify)
      n("2", "runCode", "Extract cart + check stock levels", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse cart and assess stock risk
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const phone = data.billing_address?.phone || "";
const cartTotal = parseFloat(data.total_price || 0);
const lineItems = data.line_items || [];
const itemNames = lineItems.map(i => i.title || i.name).join(", ");
const checkoutUrl = data.abandoned_checkout_url || "";
// Check stock levels from line items (Shopify includes variant inventory)
let lowestStock = Infinity;
let oosItems = [];
let lowStockItems = [];
lineItems.forEach(item => {
  const qty = parseInt(item.inventory_quantity || item.quantity_available || 999);
  if (qty <= 0) oosItems.push(item.title || item.name);
  else if (qty <= 5) lowStockItems.push(item.title || item.name);
  if (qty < lowestStock) lowestStock = qty;
});
let stockRisk;
if (oosItems.length > 0) stockRisk = "oos";
else if (lowStockItems.length > 0 || lowestStock <= 5) stockRisk = "low";
else stockRisk = "healthy";
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  cart_total: cartTotal,
  cart_items: itemNames,
  checkout_url: checkoutUrl,
  stock_risk: stockRisk,
  lowest_stock_qty: lowestStock === Infinity ? 999 : lowestStock,
  oos_items: oosItems.join(", ") || "none",
  low_stock_items: lowStockItems.join(", ") || "none",
  is_critical: (lowestStock <= 2 && lowestStock > 0) ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "stock_risk", type: "string" },
            { name: "lowest_stock_qty", type: "number" },
            { name: "oos_items", type: "string" },
            { name: "low_stock_items", type: "string" },
            { name: "is_critical", type: "string" },
          ],
        },
      }),

      // 3. Stock condition
      n("3", "condition", "Low stock risk detected?", 520, {
        conditionData: [
          {
            variable: "stock_risk",
            value: "low",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Low-stock urgency email
      emailNode(
        "4",
        "Send low-stock urgency recovery email",
        760,
        "{customer_email}",
        "Only {lowest_stock_qty} left — your cart items are almost gone, {customer_name}!",
        "<p>Hi {customer_name},</p><p><strong>Stock alert!</strong> Items in your cart are running low:</p><p>Low stock: {low_stock_items}</p><p>Only <strong>{lowest_stock_qty} remaining</strong>. Your ${cart_total} cart won't last long.</p><p><a href='{checkout_url}'>Secure Your Items Now</a></p>",
        460,
      ),

      // 5. OOS substitute/waitlist
      emailNode(
        "5",
        "Send OOS substitute + waitlist email",
        760,
        "{customer_email}",
        "Some items in your cart are out of stock, {customer_name}",
        "<p>Hi {customer_name},</p><p>We're sorry — the following items from your cart are currently out of stock: <strong>{oos_items}</strong></p><p>Join the waitlist to be notified when they're back, or browse similar items.</p><p>Your remaining cart (${cart_total}): {cart_items}</p><p><a href='{checkout_url}'>Update Your Cart</a> | <a href='https://{shopify_store}.myshopify.com/collections/all'>Browse Alternatives</a></p>",
        840,
      ),

      // 6. Healthy standard email
      emailNode(
        "6",
        "Send standard recovery email (healthy stock)",
        760,
        "{customer_email}",
        "Your cart is waiting for you, {customer_name}",
        "<p>Hi {customer_name},</p><p>You left some great items in your cart (${cart_total}). Good news — everything is in stock and ready to ship!</p><p>Items: {cart_items}</p><p><a href='{checkout_url}'>Complete Your Order</a></p>",
        1220,
      ),

      // 7. SMS for critical (Twilio)
      smsNode(
        "7",
        "SMS: Critical stock alert",
        1000,
        "{customer_phone}",
        "STOCK ALERT: Only {lowest_stock_qty} left of {low_stock_items} in your cart! Complete checkout now: {checkout_url}",
      ),

      // 8. Stock vs conversion logging (Sheets)
      sheetsNode(
        "8",
        "Log stock risk vs conversion data",
        1240,
        "CartRecoveryStockRisk",
        '[["{customer_email}","{customer_name}","{cart_total}","{cart_items}","{stock_risk}","{lowest_stock_qty}","{oos_items}","{low_stock_items}","{is_critical}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // low stock -> urgency email
      e("3", "6", "output_2"), // not low -> healthy or OOS
      e("4", "7"),
      e("5", "8"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 364. CART RECOVERY WITH BROWSE SUPPRESSION (8 nodes)
  // ===================================================================

  "Cart Recovery with Browse Suppression": {
    summary:
      "Suppresses cart recovery emails while the customer is still actively browsing. Triggers on cart abandonment, checks for an active session, delays until session ends, re-checks session status, releases the recovery email only when browsing stops, applies purchase suppression, logs natural recoveries, and queues session errors to Slack.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer creates a checkout but has not completed payment — recovery is held if the customer is still browsing",
        setup:
          "In Shopify -> Webhooks, add a checkouts/create endpoint. Connect your session tracking (Segment, GA4) to provide real-time browsing status.",
        inputFields: [],
      }),

      // 2. Active session check condition
      n("2", "runCode", "Check if customer is still browsing", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract cart data and check session activity
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const phone = data.billing_address?.phone || "";
const cartTotal = parseFloat(data.total_price || 0);
const lineItems = data.line_items || [];
const itemNames = lineItems.map(i => i.title || i.name).join(", ");
const checkoutUrl = data.abandoned_checkout_url || "";
// Session activity check
const lastActivity = data.last_activity_at || data.updated_at || "";
const now = new Date();
const lastActive = lastActivity ? new Date(lastActivity) : new Date(0);
const minutesSinceActivity = Math.round((now.getTime() - lastActive.getTime()) / 60000);
const isStillBrowsing = minutesSinceActivity < 15; // Active within 15 min
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  cart_total: cartTotal,
  cart_items: itemNames,
  checkout_url: checkoutUrl,
  is_still_browsing: isStillBrowsing ? "yes" : "no",
  minutes_since_activity: minutesSinceActivity,
  session_status: isStillBrowsing ? "active" : "ended",
  initial_check_time: now.toISOString()
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "is_still_browsing", type: "string" },
            { name: "minutes_since_activity", type: "number" },
            { name: "session_status", type: "string" },
            { name: "initial_check_time", type: "string" },
          ],
        },
      }),

      // 3. Active session check condition
      n("3", "condition", "Customer still browsing?", 520, {
        conditionData: [
          {
            variable: "is_still_browsing",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Session end delay
      n("4", "delayAction", "Wait for session to end (30 min)", 760, {
        delayActionData: {
          delayType: "fixed",
          delayValue: 30,
          delayUnit: "minutes",
        },
      }),

      // 5. Release recovery email
      emailNode(
        "5",
        "Send recovery email after session ends",
        1000,
        "{customer_email}",
        "Still thinking about {cart_items}, {customer_name}?",
        "<p>Hi {customer_name},</p><p>We noticed you were browsing earlier and left some items in your cart (${cart_total}).</p><p>Items: {cart_items}</p><p>Everything is saved and ready for you.</p><p><a href='{checkout_url}'>Complete Your Order</a></p>",
      ),

      // 6. Purchase suppression check
      n("6", "condition", "Purchased during browse session?", 1240, {
        conditionData: [
          {
            variable: "session_status",
            value: "ended",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Natural recovery logging
      sheetsNode(
        "7",
        "Log natural vs email-driven recovery",
        1480,
        "BrowseSuppressionLog",
        '[["{customer_email}","{customer_name}","{cart_total}","{cart_items}","{is_still_browsing}","{minutes_since_activity}","{session_status}","{initial_check_time}"]]',
      ),

      // 8. Session error queue (Slack)
      slackNode(
        "8",
        "Slack: Session tracking anomaly alert",
        1720,
        "Browse suppression triggered for {customer_name} ({customer_email})\\nCart: ${cart_total} | Status: {session_status}\\nMinutes since activity: {minutes_since_activity}\\nItems: {cart_items}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // still browsing -> delay
      e("3", "5", "output_2"), // session ended -> send immediately
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // session ended normally -> log
      e("6", "8", "output_2"), // anomaly -> Slack alert
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 365. CART RECOVERY WITH DYNAMIC RECOMMENDATIONS (8 nodes)
  // ===================================================================

  "Cart Recovery with Dynamic Recommendations": {
    summary:
      "Enhances cart recovery with AI-driven product recommendations. Triggers on abandonment, builds recommendation context via runCode, generates AI dynamic recommendations, enriches with real-time price and stock from Shopify, sends a recovery email with recs, tracks recommendation clicks, attributes cross-sell revenue, and scores recommendation quality to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer abandons a cart — the recovery email includes AI-generated product recommendations based on cart context",
        setup:
          "In Shopify -> Webhooks, add a checkouts/create endpoint. The flow uses cart items as context for AI recommendations.",
        inputFields: [],
      }),

      // 2. Recommendation context build (runCode)
      n("2", "runCode", "Build recommendation context from cart", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build rich context for AI recommendations
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const cartTotal = parseFloat(data.total_price || 0);
const lineItems = data.line_items || [];
const checkoutUrl = data.abandoned_checkout_url || "";
// Build product context
const products = lineItems.map(item => ({
  name: item.title || item.name,
  price: parseFloat(item.price || 0),
  category: item.product_type || "",
  vendor: item.vendor || ""
}));
const categories = [...new Set(products.map(p => p.category).filter(Boolean))];
const brands = [...new Set(products.map(p => p.vendor).filter(Boolean))];
const priceRange = products.length > 0
  ? { min: Math.min(...products.map(p => p.price)), max: Math.max(...products.map(p => p.price)) }
  : { min: 0, max: 100 };
const result = {
  customer_email: email,
  customer_name: name,
  cart_total: cartTotal,
  cart_items: products.map(p => p.name).join(", "),
  checkout_url: checkoutUrl,
  item_count: products.length,
  categories: categories.join(", "),
  brands: brands.join(", "),
  price_min: priceRange.min,
  price_max: priceRange.max,
  avg_price: products.length > 0 ? Math.round(products.reduce((s, p) => s + p.price, 0) / products.length) : 0,
  rec_context: JSON.stringify({ products: products.slice(0, 5), categories, brands })
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "item_count", type: "number" },
            { name: "categories", type: "string" },
            { name: "brands", type: "string" },
            { name: "price_min", type: "number" },
            { name: "price_max", type: "number" },
            { name: "avg_price", type: "number" },
            { name: "rec_context", type: "string" },
          ],
        },
      }),

      // 3. AI dynamic recommendations
      aiNode(
        "3",
        "AI generate dynamic product recommendations",
        520,
        "You are a product recommendation engine. Based on the customer's cart items, categories, brands, and price range, recommend 3 complementary products they would also like. Recommendations should: (1) complement existing cart items, (2) be in a similar price range, (3) add value to the overall purchase. Respond JSON: {rec_1_name: string, rec_1_reason: string, rec_2_name: string, rec_2_reason: string, rec_3_name: string, rec_3_reason: string, rec_strategy: string, cross_sell_pitch: string}",
        "Cart items: {cart_items} | Categories: {categories} | Brands: {brands} | Price range: ${price_min}-${price_max} | Avg: ${avg_price} | Context: {rec_context}",
        [
          { field: "rec_1_name", as: "rec_1_name", type: "string" },
          { field: "rec_1_reason", as: "rec_1_reason", type: "string" },
          { field: "rec_2_name", as: "rec_2_name", type: "string" },
          { field: "rec_2_reason", as: "rec_2_reason", type: "string" },
          { field: "rec_3_name", as: "rec_3_name", type: "string" },
          { field: "rec_3_reason", as: "rec_3_reason", type: "string" },
          { field: "rec_strategy", as: "rec_strategy", type: "string" },
          { field: "cross_sell_pitch", as: "cross_sell_pitch", type: "string" },
        ],
      ),

      // 4. Enrichment with price/stock (Shopify)
      shopifyNode(
        "4",
        "Enrich recommendations with live Shopify data",
        760,
        "GET",
        "products.json?title={rec_1_name}&limit=3&fields=id,title,variants,images,handle",
        [],
        [
          {
            name: "products[0].variants[0].price",
            customName: "rec_1_price",
            type: "string",
          },
          {
            name: "products[0].handle",
            customName: "rec_1_handle",
            type: "string",
          },
          {
            name: "products[0].variants[0].inventory_quantity",
            customName: "rec_1_stock",
            type: "number",
          },
          {
            name: "products[0].images[0].src",
            customName: "rec_1_image",
            type: "string",
          },
        ],
      ),

      // 5. Recovery + recs email
      emailNode(
        "5",
        "Send recovery email with recommendations",
        1000,
        "{customer_email}",
        "Your cart + 3 items we think you'll love, {customer_name}",
        "<p>Hi {customer_name},</p><p>Your cart (${cart_total}) is still waiting! Here are some items that pair perfectly with your selections:</p><p><strong>Your cart:</strong> {cart_items}</p><p><strong>You might also like:</strong></p><ul><li>{rec_1_name} (${rec_1_price}) — {rec_1_reason}</li><li>{rec_2_name} — {rec_2_reason}</li><li>{rec_3_name} — {rec_3_reason}</li></ul><p>{cross_sell_pitch}</p><p><a href='{checkout_url}'>Complete Your Order</a></p>",
      ),

      // 6. Recommendation click tracking
      sheetsNode(
        "6",
        "Track recommendation click events",
        1240,
        "RecClicks",
        '[["{customer_email}","{rec_1_name}","{rec_1_price}","{rec_2_name}","{rec_3_name}","{rec_strategy}"]]',
      ),

      // 7. Cross-sell attribution
      n("7", "runCode", "Calculate cross-sell attribution", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the cross-sell recommendation relevance
const cartTotal = parseFloat(vars.cart_total) || 0;
const recPrice = parseFloat(vars.rec_1_price) || 0;
const avgPrice = parseFloat(vars.avg_price) || 0;
// Price proximity score (how close rec is to cart avg)
const priceDiff = Math.abs(recPrice - avgPrice);
const priceScore = avgPrice > 0 ? Math.max(0, 100 - Math.round((priceDiff / avgPrice) * 100)) : 50;
const result = {
  cross_sell_potential: recPrice,
  upsell_value: Math.round((recPrice / cartTotal) * 100),
  price_relevance_score: priceScore,
  attribution_channel: "cart_recovery_dynamic_recs",
  attribution_timestamp: new Date().toISOString()
};
return result;`,
          outputVariables: [
            { name: "cross_sell_potential", type: "number" },
            { name: "upsell_value", type: "number" },
            { name: "price_relevance_score", type: "number" },
            { name: "attribution_channel", type: "string" },
            { name: "attribution_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Quality scoring (Sheets)
      sheetsNode(
        "8",
        "Log recommendation quality scores",
        1720,
        "CartRecoveryDynamicRecs",
        '[["{customer_email}","{customer_name}","{cart_total}","{cart_items}","{rec_1_name}","{rec_1_price}","{rec_2_name}","{rec_3_name}","{rec_strategy}","{cross_sell_potential}","{price_relevance_score}","{attribution_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 366. CART RECOVERY WITH REVIEW SNIPPETS (7 nodes)
  // ===================================================================

  "Cart Recovery with Review Snippets": {
    summary:
      "Enhances cart recovery with curated product review snippets. Triggers on cart abandonment, pulls product reviews, filters for quality via runCode, selects the best snippets, sends a review-enhanced recovery email, applies a standard fallback condition for products without reviews, and logs proof-value comparison data to Sheets.",
    nodes: [
      trigger("Webhook: Cart abandonment detected", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "checkouts/create",
        howItStarts:
          "A customer abandons a cart — recovery email includes curated review snippets for carted products",
        setup:
          "In Shopify -> Webhooks, add a checkouts/create endpoint. Connect your review platform (Judge.me, Yotpo, Stamped) API for review data access.",
        inputFields: [],
      }),

      // 2. Product review pull
      n("2", "hitApi", "Pull reviews for carted products", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://judge.me/api/v1/reviews?shop_domain={shopify_store}.myshopify.com&per_page=15&sort=rating&direction=desc",
          headers: [
            { key: "Authorization", value: "Bearer {judgeme_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "reviews[0].body",
              customName: "raw_review_1",
              type: "string",
            },
            {
              name: "reviews[0].rating",
              customName: "raw_rating_1",
              type: "number",
            },
            {
              name: "reviews[0].reviewer.name",
              customName: "raw_reviewer_1",
              type: "string",
            },
            {
              name: "reviews[1].body",
              customName: "raw_review_2",
              type: "string",
            },
            {
              name: "reviews[1].rating",
              customName: "raw_rating_2",
              type: "number",
            },
            {
              name: "reviews[1].reviewer.name",
              customName: "raw_reviewer_2",
              type: "string",
            },
            {
              name: "reviews[2].body",
              customName: "raw_review_3",
              type: "string",
            },
            {
              name: "reviews[2].rating",
              customName: "raw_rating_3",
              type: "number",
            },
            {
              name: "reviews.length",
              customName: "raw_review_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Quality filtering (runCode)
      n("3", "runCode", "Filter and select best review snippets", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select best review snippets for cart recovery email
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || "";
const name = data.billing_address?.first_name || data.customer?.first_name || "there";
const cartTotal = parseFloat(data.total_price || 0);
const lineItems = data.line_items || [];
const itemNames = lineItems.map(i => i.title || i.name).join(", ");
const checkoutUrl = data.abandoned_checkout_url || "";
// Score and filter reviews
const candidates = [
  { text: vars.raw_review_1 || "", rating: parseInt(vars.raw_rating_1) || 0, author: vars.raw_reviewer_1 || "Customer" },
  { text: vars.raw_review_2 || "", rating: parseInt(vars.raw_rating_2) || 0, author: vars.raw_reviewer_2 || "Customer" },
  { text: vars.raw_review_3 || "", rating: parseInt(vars.raw_rating_3) || 0, author: vars.raw_reviewer_3 || "Customer" },
].filter(r => r.rating >= 4 && r.text.length >= 15);
// Truncate long reviews to snippet length
const snippets = candidates.map(r => ({
  ...r,
  snippet: r.text.length > 150 ? r.text.substring(0, 147) + "..." : r.text
}));
const hasReviews = snippets.length > 0;
const result = {
  customer_email: email,
  customer_name: name,
  cart_total: cartTotal,
  cart_items: itemNames,
  checkout_url: checkoutUrl,
  snippet_1: snippets[0] ? snippets[0].snippet : "",
  snippet_1_author: snippets[0] ? snippets[0].author : "",
  snippet_1_rating: snippets[0] ? snippets[0].rating : 0,
  snippet_2: snippets[1] ? snippets[1].snippet : "",
  snippet_2_author: snippets[1] ? snippets[1].author : "",
  has_quality_reviews: hasReviews ? "yes" : "no",
  snippet_count: snippets.length,
  total_available: parseInt(vars.raw_review_count) || 0
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "cart_total", type: "number" },
            { name: "cart_items", type: "string" },
            { name: "checkout_url", type: "string" },
            { name: "snippet_1", type: "string" },
            { name: "snippet_1_author", type: "string" },
            { name: "snippet_1_rating", type: "number" },
            { name: "snippet_2", type: "string" },
            { name: "snippet_2_author", type: "string" },
            { name: "has_quality_reviews", type: "string" },
            { name: "snippet_count", type: "number" },
            { name: "total_available", type: "number" },
          ],
        },
      }),

      // 4. Snippet selection — AI enhancement
      aiNode(
        "4",
        "AI enhance snippets for recovery email",
        760,
        "You are a conversion copywriter. Given product review snippets and cart items, write a cart recovery email that weaves in the review quotes naturally. Use the reviews as social proof to build trust. If snippets are available, lead with them. If not, write a standard recovery email. Respond JSON: {email_subject: string, email_body: string, proof_section: string, email_type: string}",
        'Customer: {customer_name} | Cart: {cart_items} (${cart_total}) | Review 1: \\"{snippet_1}\\" — {snippet_1_author} ({snippet_1_rating} stars) | Review 2: \\"{snippet_2}\\" — {snippet_2_author} | Has reviews: {has_quality_reviews} | Total reviews: {total_available}',
        [
          { field: "email_subject", as: "review_subject", type: "string" },
          { field: "email_body", as: "review_body", type: "string" },
          { field: "proof_section", as: "proof_section", type: "string" },
          { field: "email_type", as: "email_type", type: "string" },
        ],
      ),

      // 5. Review-enhanced email
      emailNode(
        "5",
        "Send review-enhanced recovery email",
        1000,
        "{customer_email}",
        "{review_subject}",
        "<p>Hi {customer_name},</p><p>{review_body}</p><p>{proof_section}</p><p>Your cart (${cart_total}): {cart_items}</p><p><a href='{checkout_url}'>Complete Your Order</a></p>",
      ),

      // 6. Standard fallback condition
      n("6", "condition", "Had quality review snippets?", 1240, {
        conditionData: [
          {
            variable: "has_quality_reviews",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Proof value comparison logging (Sheets)
      sheetsNode(
        "7",
        "Log proof value comparison data",
        1480,
        "CartRecoveryReviewSnippets",
        '[["{customer_email}","{customer_name}","{cart_total}","{cart_items}","{has_quality_reviews}","{snippet_count}","{total_available}","{snippet_1_author}","{snippet_1_rating}","{email_type}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // had reviews -> log with proof metrics
      e("6", "7", "output_2"), // no reviews -> log as fallback standard
    ],
  },

  // ===================================================================
  // 367. CATEGORY BROWSE ABANDONMENT (7 nodes)
  // ===================================================================

  "Category Browse Abandonment": {
    summary:
      "Recovers visitors who browsed a category page but left without viewing individual products. Triggers on category page exit, pulls collection metadata from Shopify, selects top products via runCode, generates an AI category story, sends a curated category email, tracks clicks, and logs category recovery performance to Sheets.",
    nodes: [
      trigger("Webhook: Category page exit without PDP view", "webhook_app", {
        source: "Website Analytics / Tracking Pixel",
        event: "browse.category_exit",
        howItStarts:
          "A known visitor exits after viewing a category/collection page without clicking into any individual product detail pages",
        setup:
          "Configure your site tracking to detect category-only browse sessions (viewed collection but no PDP clicks) and POST the session data (category viewed, visitor identity, time on page) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Collection metadata pull (Shopify)
      shopifyNode(
        "2",
        "Pull collection metadata from Shopify",
        280,
        "GET",
        "collections/{collection_id}.json",
        [],
        [
          {
            name: "collection.title",
            customName: "collection_title",
            type: "string",
          },
          {
            name: "collection.body_html",
            customName: "collection_description",
            type: "string",
          },
          {
            name: "collection.handle",
            customName: "collection_handle",
            type: "string",
          },
          {
            name: "collection.image.src",
            customName: "collection_image",
            type: "string",
          },
          {
            name: "collection.products_count",
            customName: "collection_product_count",
            type: "number",
          },
        ],
      ),

      // 3. Top product selection (runCode)
      n("3", "runCode", "Select top products for category recovery", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract visitor data and select top products for recovery
const payload = vars.webhook_payload || "{}";
let data;
try { data = JSON.parse(payload); } catch(e) { data = {}; }
const email = data.email || data.customer_email || data.visitor_email || "";
const name = data.name || data.customer_name || "there";
const phone = data.phone || "";
const category = vars.collection_title || data.category || "";
const collectionHandle = vars.collection_handle || "";
const timeOnPage = data.time_on_page || data.session_duration || 0;
const scrollDepth = data.scroll_depth || 0;
// Select products based on popularity (would query Shopify in production)
const productCount = parseInt(vars.collection_product_count) || 0;
const result = {
  customer_email: email,
  customer_name: name,
  customer_phone: phone,
  category_name: category,
  collection_handle: collectionHandle,
  collection_description: (vars.collection_description || "").replace(/<[^>]*>/g, "").substring(0, 200),
  collection_image: vars.collection_image || "",
  time_on_page_seconds: timeOnPage,
  scroll_depth_pct: scrollDepth,
  product_count: productCount,
  engagement_level: timeOnPage > 60 ? "high" : timeOnPage > 20 ? "medium" : "low",
  collection_url: "https://" + (vars.shopify_store || "store") + ".myshopify.com/collections/" + collectionHandle
};
return result;`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "category_name", type: "string" },
            { name: "collection_handle", type: "string" },
            { name: "collection_description", type: "string" },
            { name: "collection_image", type: "string" },
            { name: "time_on_page_seconds", type: "number" },
            { name: "scroll_depth_pct", type: "number" },
            { name: "product_count", type: "number" },
            { name: "engagement_level", type: "string" },
            { name: "collection_url", type: "string" },
          ],
        },
      }),

      // 4. AI category story
      aiNode(
        "4",
        "AI craft category curation email",
        760,
        "You are an e-commerce category merchandiser. The visitor browsed a collection page but did not click into any products. Write an email that tells the story of this collection — what makes it special, who it is for, and why they should explore further. Curate 3 standout products from the collection to highlight. Be editorial, not salesy. Respond JSON: {email_subject: string, email_body: string, featured_product_1: string, featured_product_2: string, featured_product_3: string, category_story: string, cta_text: string}",
        "Customer: {customer_name} | Category: {category_name} | Description: {collection_description} | Products in collection: {product_count} | Time on page: {time_on_page_seconds}s | Scroll depth: {scroll_depth_pct}% | Engagement: {engagement_level}",
        [
          { field: "email_subject", as: "cat_subject", type: "string" },
          { field: "email_body", as: "cat_body", type: "string" },
          { field: "featured_product_1", as: "featured_1", type: "string" },
          { field: "featured_product_2", as: "featured_2", type: "string" },
          { field: "featured_product_3", as: "featured_3", type: "string" },
          { field: "category_story", as: "category_story", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),

      // 5. Category curation email
      emailNode(
        "5",
        "Send category curation recovery email",
        1000,
        "{customer_email}",
        "{cat_subject}",
        "<p>Hi {customer_name},</p><p>{cat_body}</p><p><strong>Our picks from {category_name}:</strong></p><ol><li>{featured_1}</li><li>{featured_2}</li><li>{featured_3}</li></ol><p>{category_story}</p><p><a href='{collection_url}'>{cta_text}</a></p>",
      ),

      // 6. Click tracking
      sheetsNode(
        "6",
        "Track category email click engagement",
        1240,
        "CategoryRecoveryClicks",
        '[["{customer_email}","{category_name}","{featured_1}","{featured_2}","{featured_3}","{engagement_level}"]]',
      ),

      // 7. Category recovery logging (Sheets)
      sheetsNode(
        "7",
        "Log category recovery performance",
        1480,
        "CategoryBrowseRecovery",
        '[["{customer_email}","{customer_name}","{category_name}","{collection_handle}","{product_count}","{time_on_page_seconds}","{scroll_depth_pct}","{engagement_level}","{featured_1}","{featured_2}","{featured_3}","{category_story}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
